// rune-icon-engine.ts — verbatim port of the rune-icon renderer (was inline in
// admin_tools/rune_icon_preview.php). Carved out 2026-06-01 byte-faithfully: the
// compile / render / glyph / svg / catalog logic is unchanged. ONLY the data source
// changed — LAYER_VISUAL / STATUS_META / ICON_CDN now come from window.FT_RUNE via
// ./data instead of per-page PHP-injected consts. Public API lives in index.ts.
//
// Kept as ONE module on purpose: the original source interleaves catalogs, glyphs,
// compile and render, so a finer file split would mean reassembling many
// non-contiguous slices by hand — needless risk to the signed-off visuals. esbuild
// bundles this into includes/js/dist/rune-icon-bundle.js either way; the finer split
// is a safe zero-risk follow-up on working code.
import { CIRCLE_COLOR } from './circle-color';
import { ICON_CDN, LAYER_VISUAL, STATUS_META } from './data';
import type { AbilityArr, LayerVisual, RuneOpts } from './types';

type RuneItemRef = {
  items?: { item_name?: string; item_type?: string };
  picture_url?: string;
  [key: string]: unknown;
};
export type RuneStatusEntry = {
  it: RuneItemRef;
  mode: string;
  who: string;
  timing: string;
};
export type RuneIR = {
  name: string;
  id: string | null;
  itype: string;
  isSpell: boolean;
  sphere: string | null;
  sphereAll: string[];
  circle: string | null;
  manifestation: string | null;
  halfOnContest: boolean;
  spellContest: 'adv' | 'dis' | null;
  contestVsStatus: Array<{ name: string; adv: boolean }>;
  immuneStatus: string[];
  rollAdvDis: Array<{ rt: string; adv: boolean; against: boolean }>;
  hasAgainst: boolean;
  cast: string;
  roll: string | null;
  damage: string[];
  dice: number;
  die: number;
  mod: number;
  dynMod: boolean;
  modRaw: string;
  avg: number | null;
  aoe: string | null;
  aoeDist: number | null;
  range: string | null;
  durType: string;
  durInt: number;
  concentration: boolean;
  ritual: boolean;
  sameTeam: boolean;
  oppTeam: boolean;
  persistentAoe: boolean;
  isAura: boolean;
  speedChanges: Array<{ kind: string; up: boolean; val: string }>;
  speedGrants: Array<{ kind: string; val: string }>;
  shapeshift: string[];
  level: number | null;
  hasTrigger: boolean;
  trigLabel: string;
  contesterSkills: string[];
  contesteeSkills: string[];
  disarmed: boolean;
  reqClass: string[];
  reqSubclass: string[];
  reqClassLevel: number | null;
  resist: string[];
  immune: string[];
  vuln: string[];
  apChange: string | null;
  grantedAbilities: Array<{ name: string; type: string }>;
  fixedDc: number | null;
  moveToward: number | null;
  targetRetries: boolean;
  skillAdv: boolean;
  skillDis: boolean;
  skillAdvNames: string[];
  skillDisNames: string[];
  resetsLong: boolean;
  resetsShort: boolean;
  rechargeBegin: number | null;
  rechargeEnd: number | null;
  charges: number | null;
  teleportChange: string | null;
  consumesCharges: number | null;
  addsCharges: number | null;
  consumesName: string | null;
  addsName: string | null;
  light: 'dim' | 'bright' | null;
  destroysLight: boolean;
  fallNegate: string | null;
  limitedVis: string | null;
  conditionStatuses: Array<{ name: string; who: string; not: boolean }>;
  condition: string | null;
  rollManip: string | null;
  shortCaster: boolean;
  shortTarget: boolean;
  summon: string[];
  fabricate: string[];
  coreImg: string | null;
  _rawKeys: string[];
  _statusKeys: string[];
  statusObjs: RuneItemRef[];
  statusEntries: RuneStatusEntry[];
};
type StatusRuneIR = {
  name: string;
  icon: string;
  stacks: number | null;
  motif: string;
  color: string;
  boon: boolean;
  persistent: boolean;
};
type SvgAttrs = Record<string, string | number>;
type RuneCatalogEntry = {
  item_name?: string;
  item_type?: string;
  items?: { item_name?: string; item_type?: string };
};
type RuneEngineWindow = Window & {
  FT_ICON_REGISTRY?: unknown;
  ICON_LOOKUP?: unknown;
  ftItemNameById?: (id: string) => string;
};
type IconRegistryEntry = { file?: string };
type Point = [number, number];
type Hsl = [number, number, number];
/* Family → base hue. Grouped into clusters (offense=reds, defense=blues, control=purples,
   magic/resource=indigos, movement=teals, area/material=greens, utility=mixed); the per-layer
   GLYPH does the fine distinction within a family. ~first pass, tuned by eye on the preview. */
const FAMILY_HUE:Record<string,string>={
  /* offense */    damage:'#ef4444', crit:'#f97316', weapon:'#fb7185', action:'#fdba74',
  /* defense */    defense:'#3b82f6', armor:'#7dd3fc', contest:'#06b6d4', roll:'#22d3ee',
  /* control */    status:'#a855f7', condition:'#8b5cf6', restriction:'#c026d3', curse:'#d946ef',
  /* magic/res */  spell:'#818cf8', menta:'#a78bfa', essence:'#c084fc', sanguine:'#be123c',
                   charge:'#eab308', ability:'#7aa2ff', trigger:'#fbbf24', light:'#fde047',
  /* movement */   movement:'#14b8a6', senses:'#6366f1', range:'#5eead4',
  /* area/time */  aoe:'#22c55e', duration:'#64748b',
  /* material */   summon:'#16a34a', crafting:'#a16207', natural:'#65a30d', terrain:'#78716c', size:'#a8a29e',
  /* utility */    skill:'#0ea5e9', feat:'#f472b6', survival:'#84cc16', effect:'#94a3b8',
                   language:'#9ca3af', object:'#737373', misc:'#6b7280'};
/* Layers ALREADY drawn by a curated zone (compile()/renderSigil consume these) — the generic tier
   skips them so curated always wins and there's no double-draw. Exact set + prefix patterns. */
const CURATED_EXACT=new Set(['dice','die','mod','dice_upcast','die_upcast','mod_upcast',
  'spell_sphere','spell_circle','spell_casting_time','spell_level','spell_concentration','spell_ritual',
  'roll_type','damage_type','aoe_type','aoe_distance','aoe_follows_target','persistent_aoe','range',
  'duration_type','duration_int','effect_only_affects_same_team_type','effect_only_affects_opposing_team_type',
  'skill_roll_contester','skill_roll_contestee','skill_roll_adv','skill_roll_dis','disarmed',
  'damage_type_resistance','damage_type_immunity','damage_type_vulnerability',
  'ap_change_during_turn','reaction_reset','ability_dc','caster_moves_towards_target_in_ft',
  'target_retries_contesting_roll_on_end_of_turn','ability_resets_long_rest','ability_resets_short_rest',
  'recharge_range_begin','recharge_range_end','consumes_ability_charges','consumes_charges_qty',
  'add_charges_to_ability','add_charges_qty','create_light_radius','bright_light_dim_light',
  'create_dim_light_only','effect_can_destroy_light','negate_falling_damage_in_feet','limited_visibility_ft',
  'summon','fabricate','menta_mental_manifestation','contesting_roll_successful_for_half_damage',
  'contesting_roll_adv_vs_spells','contesting_roll_dis_vs_spells',
  'contesting_roll_adv_vs_status_effect','contesting_roll_dis_vs_status_effect','immune_status_effect',
  'roll_type_adv','roll_type_dis','roll_type_against_adv','roll_type_against_dis',
  'menta_level', // drawn as the bottom-center LEVEL plaque (like spell_level), not a generic token
  'ability_activation_charges','ability_charges', // read for the charge-pool dots near the moon, not generic tokens
  'full_ability','attack_ability','quick_ability','free_ability','reaction_ability',
  'opportunity_attack_ability','ability','passive_ability',
  'effect_ends_on_start_of_caster_next_turn','effect_ends_on_end_of_caster_next_turn',
  'effect_ends_on_target_start_of_turn','effect_ends_on_target_end_of_turn']);
const CURATED_RX=[/^status_effect_/,/^triggers_/,/^if_(target|caster)_has_status_effect/,
  /^speed_(walking|flying|swimming|climbing|burrowing|hovering|jumping)$/,
  /^change_speed_\w+_remaining$/,/^user_choice_\d+_(summon|shapeshift|menta)$/,
  /^roll_type_(roll_result_is_|is_rolled_at_)/]; // only the roll-MANIP ones are curated (starburst);
                                                  // roll_type_adv/dis/against_* fall through to generic tokens
const isCuratedLayer=(n:string):boolean=>CURATED_EXACT.has(n)||CURATED_RX.some(rx=>rx.test(n));
const prettyLayer=(n:string):string=>n.replace(/^user_choice_\d+_/,'').replace(/_/g,' ');
/* FNV-1a string hash → stable per-layer glyph/variant so a layer reads the SAME everywhere. */
function gHash(s:string):number{let h=2166136261>>>0;for(let i=0;i<s.length;i++){h^=s.charCodeAt(i);h=Math.imul(h,16777619)>>>0;}return h>>>0;}
/* per-layer COLOUR variation within a family: shift hue/lightness by hash so sibling tokens sitting
   close together are visually distinct at a distance, while staying in the family's colour neighbourhood. */
function hexToHsl(h:string):Hsl{h=h.replace('#','');const r=parseInt(h.slice(0,2),16)/255,g=parseInt(h.slice(2,4),16)/255,b=parseInt(h.slice(4,6),16)/255;
  const mx=Math.max(r,g,b),mn=Math.min(r,g,b);let hh=0,s=0,l=(mx+mn)/2;
  if(mx!==mn){const d=mx-mn;s=l>0.5?d/(2-mx-mn):d/(mx+mn);
    hh=(mx===r?((g-b)/d+(g<b?6:0)):mx===g?((b-r)/d+2):((r-g)/d+4))*60;}
  return[hh,s,l];}
function hslToHex(h:number,s:number,l:number):string{h=((h%360)+360)%360/360;const f=(n:number):string=>{const k=(n+h*12)%12,a=s*Math.min(l,1-l);
  const c=l-a*Math.max(-1,Math.min(k-3,9-k,1));return Math.round(c*255).toString(16).padStart(2,'0');};
  return '#'+f(0)+f(8)+f(4);}
function shade(hex:string,seed:number):string{const[h,s,l]=hexToHsl(hex);
  return hslToHex(h+((seed%7)-3)*15, Math.max(0.45,s), Math.max(0.4,Math.min(0.72,l+((Math.floor(seed/7)%5)-2)*0.06)));}
/* 8 simple generative marks chosen by hash — the rune-forge "channels" idea, minimal at small size. */
function genGlyphMark(g:SVGElement,x:number,y:number,r:number,name:string,hue:string):void{
  const o={stroke:hue,'stroke-width':1,fill:'none','stroke-linecap':'round','stroke-linejoin':'round'};
  /* SEMANTIC overrides for a few recognizable layers (rest stay hash-driven). */
  const base=name.replace(/^user_choice_\d+_/,'');
  if(base==='spell_component_verbal'){ // mouth = upside-down (open) chevron
    g.appendChild(el('path',{d:`M${x-r} ${y-r*0.5} L${x} ${y+r*0.6} L${x+r} ${y-r*0.5}`,...o}));return;}
  if(base==='spell_component_somatic'){ // hand = three short finger strokes
    g.appendChild(el('path',{d:`M${x-r*0.6} ${y+r} V${y-r*0.3} M${x} ${y+r} V${y-r} M${x+r*0.6} ${y+r} V${y-r*0.3}`,...o}));return;}
  if(base==='spell_upcastable'){ // up arrow = can be cast at a higher level
    g.appendChild(el('path',{d:`M${x} ${y+r} V${y-r} M${x-r*0.62} ${y-r*0.35} L${x} ${y-r} L${x+r*0.62} ${y-r*0.35}`,...o}));return;}
  if(base==='damage_heals_caster'){ // little pink heart
    g.appendChild(el('path',{d:`M${x} ${y+r*0.95} C${x-r*1.45} ${y-r*0.35} ${x-r*0.55} ${y-r*1.15} ${x} ${y-r*0.25} C${x+r*0.55} ${y-r*1.15} ${x+r*1.45} ${y-r*0.35} ${x} ${y+r*0.95} Z`,fill:'#f472b6',stroke:'none'}));return;}
  /* ROLL TYPE ADV/DIS — emphasised (these matter): bold double-chevron, green UP = advantage / red DOWN = disadvantage. */
  if(base==='roll_type_adv'||base==='roll_type_against_adv'){const gc='#34d399',a={fill:'none',stroke:gc,'stroke-width':1.5,'stroke-linecap':'round','stroke-linejoin':'round'};
    g.appendChild(el('path',{d:`M${x-r} ${y+r*0.35} L${x} ${y-r*0.7} L${x+r} ${y+r*0.35}`,...a}));
    g.appendChild(el('path',{d:`M${x-r} ${y+r*1.05} L${x} ${y} L${x+r} ${y+r*1.05}`,...a}));return;}
  if(base==='roll_type_dis'||base==='roll_type_against_dis'){const rc='#ef5a5a',a={fill:'none',stroke:rc,'stroke-width':1.5,'stroke-linecap':'round','stroke-linejoin':'round'};
    g.appendChild(el('path',{d:`M${x-r} ${y-r*0.35} L${x} ${y+r*0.7} L${x+r} ${y-r*0.35}`,...a}));
    g.appendChild(el('path',{d:`M${x-r} ${y-r*1.05} L${x} ${y} L${x+r} ${y-r*1.05}`,...a}));return;}
  switch(gHash(name)%8){
    case 0: g.appendChild(el('circle',{cx:x,cy:y,r:r*0.7,fill:hue,stroke:'none'}));break;            // dot
    case 1: g.appendChild(el('circle',{cx:x,cy:y,r:r*0.9,...o}));break;                              // ring
    case 2: g.appendChild(el('line',{x1:x-r,y1:y,x2:x+r,y2:y,...o}));break;                          // bar
    case 3: g.appendChild(el('path',{d:`M${x-r} ${y} H${x+r} M${x} ${y-r} V${y+r}`,...o}));break;    // plus
    case 4: g.appendChild(el('path',{d:`M${x} ${y-r} L${x+r} ${y+r} L${x-r} ${y+r} Z`,...o}));break; // triangle
    case 5: g.appendChild(el('rect',{x:x-r*0.8,y:y-r*0.8,width:r*1.6,height:r*1.6,...o}));break;     // square
    case 6: g.appendChild(el('path',{d:`M${x-r} ${y+r*0.6} L${x} ${y-r*0.6} L${x+r} ${y+r*0.6}`,...o}));break; // chevron
    default:g.appendChild(el('path',{d:`M${x-r} ${y} Q${x-r*0.5} ${y-r} ${x} ${y} T${x+r} ${y}`,...o}));   // wave
  }}
/* FIXED family order around the top outer arc → every family has a CONSISTENT angle on EVERY icon
   (like range: same spot every time, so it's learnable), and a family's layers CLUMP together. */
const FAMILY_ORDER=['restriction','condition','status','curse','sanguine','menta','spell','essence',
  'charge','ability','feat','skill','crit','damage','weapon','action','roll','contest','defense',
  'armor','aoe','duration','range','movement','senses','summon','crafting','natural','terrain',
  'size','light','survival','trigger','effect','language','object','misc'];
function familyAngle(f:string):number{const n=FAMILY_ORDER.length,i=FAMILY_ORDER.indexOf(f),A0=-186,A1=6;
  return A0+(A1-A0)*((i<0?n-1:i)/(n-1));}
/* GENERIC TOKEN TIER — outer arc; one token per visible, non-curated layer. Hue=family, glyph=name-hash,
   FIXED angle per family (consistent across icons) with same-family layers fanned + clumped, multi/chosen
   pips, per-family overflow = a small unobtrusive semicircle (no "+N" disc). */
function renderGenericTokens(s:SVGElement,ir:RuneIR,cx:number,cy:number,R:number):void{
  let keys:string[]=(ir._rawKeys||[]).filter((n:string)=>LAYER_VISUAL[n]&&!isCuratedLayer(n));
  /* UPCASTABLE: if the spell has ANY upcast element (the spell_upcastable flag OR any per-aspect
     upcast layer — spell_upcast_*, dice/die/mod_upcast), collapse them ALL into a single up-arrow.
     Detection keys off "any upcast layer", NOT the flag specifically, so this survives the flag
     being phased out later. (spell_upcastable used as the canonical glyph key; byFam is guarded
     below in case it's no longer in the catalog.) */
  if(keys.some((n:string)=>/upcast/i.test(n))){keys=keys.filter((n:string)=>!/upcast/i.test(n));keys.push('spell_upcastable');}
  if(!keys.length)return;
  const byFam:Record<string,string[]>={};keys.forEach((n:string)=>{const f=(LAYER_VISUAL[n]&&LAYER_VISUAL[n].f)||'spell';(byFam[f]=byFam[f]||[]).push(n);});
  const r=R+5;
  const famHue=(f:string):string=>FAMILY_HUE[f]||FAMILY_HUE.misc||'#6b7280';
  /* The contest-skill discs flank the roll emblem (caster ~-121°, target ~-59°) — a heavily-used
     zone (skill_roll_contestee). When present, nudge any generic family that would land in those
     wedges out of the way (e.g. push/pull-after-hit lives in 'movement' near the target disc). */
  const blocked:number[]=[];
  if(ir.contesterSkills&&ir.contesterSkills.length)blocked.push(-121);
  if(ir.contesteeSkills&&ir.contesteeSkills.length)blocked.push(-59);
  const clearAngle=(a:number):number=>{for(const b of blocked){if(Math.abs(a-b)<16)a=(a<b?b-16:b+16);}return a;};
  Object.keys(byFam).forEach(f=>{
    const layers=(byFam[f]||[]).sort(), base=clearAngle(familyAngle(f));
    const vis=layers.slice(0,5), kk=vis.length, spread=6.5; // wider so same-family siblings don't overlap
    vis.forEach((name:string,i:number)=>{
      const ang=base+(i-(kk-1)/2)*spread, [x,y]=P(cx,cy,r,ang), meta:Partial<LayerVisual>=LAYER_VISUAL[name]||{};
      const hue=shade(famHue(f),gHash(name)); // each layer its OWN shade within the family colour
      const tg=part(s,'Modifier',`${prettyLayer(name)} · ${f}`);
      tg.appendChild(el('circle',{cx:x,cy:y,r:3.2,fill:'#0c0f17',stroke:hue,'stroke-width':1.4}));
      genGlyphMark(tg,x,y,2,name,hue);
      if(meta.m)tg.appendChild(el('circle',{cx:x+2.5,cy:y-2.5,r:1,fill:hue,stroke:'none'}));          // list/count
      if(meta.c)tg.appendChild(el('circle',{cx:x-2.5,cy:y-2.5,r:1.1,fill:'#fff',stroke:hue,'stroke-width':0.5})); // chosen
    });
    if(layers.length>5){const ang=base+(kk-1-(kk-1)/2)*spread,[x,y]=P(cx,cy,r,ang); // semicircle under last = "more"
      const hue=famHue(f);
      const tg=part(s,'More',`+${layers.length-5} more ${f}: ${layers.slice(5).map(prettyLayer).join(', ')}`);
      tg.appendChild(el('path',{d:`M${x-3.4} ${y+3.6} A3.4 3.4 0 0 0 ${x+3.4} ${y+3.6}`,fill:'none',stroke:hue,'stroke-width':1.3}));}
  });}

/* ===== catalogs (mirror additions.php; same as the rune-forge sandbox) ===== */
/* CIRCLE_COLOR now lives in ./circle-color (single source of truth, shared with the XZ
   spellsight glow layer); imported above. */
const SPHERE_SHAPE:Record<string,string>={Arcane:'hexagon',Divine:'triangle',Primal:'square'};
const SPHERE_TEX:Record<string,string[]>={
  Arcane:['#3b82f6','#22d3ee','#818cf8','#a855f7','#38bdf8','#3b82f6'],
  Divine:['#ffffff','#cbd5e1','#8a97a8','#eef2f7','#64748b','#ffffff'],
  Primal:['#84cc16','#4d7c0f','#a16207','#15803d','#65a30d']};
/* ⚠ BUG FIX 2026-07-24 — the two maps above are keyed on the bare word, but name1() resolves
   spell_sphere through the catalog to its ITEM_NAME, which is 'Arcane Sphere of Magic' /
   'Divine Sphere of Magic' / 'Primal Sphere of Magic' (additions.php SpellSphere::LIST ~9700).
   So EVERY direct lookup missed: the shape fell back to 'circle' and the texture to the grey
   ['#9ca3af','#64748b'] default — i.e. every spell drew an identical grey circle where its
   sphere identity was supposed to be, and typeAccent2() handed the card band the primary hue
   instead of the sphere's. Resolve by KEYWORD instead, exactly like manifestColor() does for
   the menta manifestations, so it survives 'Arcane' / 'Arcane Sphere of Magic' / casing drift.
   (Circles of Magic are unaffected — CIRCLE_COLOR's keys match SpellCircle item_names exactly.) */
function sphereKey(name:string|null|undefined):string|null{
  const n=(name||'').toLowerCase();
  for(const k in SPHERE_SHAPE){if(n.includes(k.toLowerCase()))return k;}
  return null;}
const sphereShape=(n:string|null|undefined):string=>SPHERE_SHAPE[sphereKey(n)??'']||'circle';
const sphereTex=(n:string|null|undefined):string[]=>SPHERE_TEX[sphereKey(n)??'']||['#9ca3af','#64748b'];
const CIRCLE_SHAPE:Record<string,string>={
  'Circle of Abjuration':'octagon','Circle of Charm':'pentagon','Circle of Conjuration':'diamond',
  'Circle of Divination':'star','Circle of Evocation':'triangle','Circle of Illusion':'hexagon',
  'Circle of Necromancy':'chevron','Circle of Transmutation':'square'};
/* MENTA manifestations (additions.php MentalManifestation::LIST) — parallel to circle_of_magic:
   each manifestation is its own COLOUR; the INNER shape encodes the action-point cost (like a
   spell's casting-time shape). (user 2026-06-01) */
/* keyed by the manifestation KEYWORD so the lookup survives name/casing variants
   ("Manifestation Of Pyrokinesis" / "Manifestation of Pyrokinesis" / "Pyrokinesis" all match). */
const MANIFEST_COLOR:Record<string,string>={clairvoyance:'#22d3ee',metamorphosis:'#a855f7',pyrokinesis:'#ff6b35',
  telekinesis:'#fbbf24',telepathy:'#f472b6'};
function manifestColor(name:string|null|undefined):string{const n=(name||'').toLowerCase();
  for(const k in MANIFEST_COLOR){const color=MANIFEST_COLOR[k];if(color&&n.includes(k))return color;}
  return TYPE_COLOR.menta||'#818cf8';}
/* ⚠️ SINGLE SOURCE OF TRUTH for ability-type identity — THREE consumers read TYPE_SHAPE / TYPE_COLOR /
   TYPE_AP, so a change here propagates to all of them (don't change one expecting only the sigil to move):
     1. the medallion FRAME shape/colour inside renderSigil (the big rune on cards / ability bar);
     2. typeAccent() — the .ft-rune-card hero-band / glow / eyebrow colour;
     3. typeBadge() — the small ability-TYPE CHIP that replaced the flat full_ability.png / curse.png …
        icons. typeBadge() is consumed by the icon bundle (includes/js-src/icon/index.ts → ftMountIcons),
        which swaps it in for ability-type data-icon keys (both the canonical item_type AND the plural
        page/nav doc-icon forms, e.g. 'curses'→'curse'). See the "Live mount — ability-type chips" block
        in _ai_code_files/runic-icon-production-wiring-handoff.md.
   After editing any of these THREE maps, rebuild BOTH bundles: `npm run build:rune-icon` AND
   `npm run build:icon` (the chip reads typeBadge from the rune bundle; both self-push to staging). */
const TYPE_SHAPE:Record<string,string>={full_ability:'octagon',ability:'hexagon',attack_ability:'star',
  quick_ability:'diamond',free_ability:'circle',reaction_ability:'pentagon',
  opportunity_attack_ability:'chevron',passive_ability:'square',
  curse:'triangle_down',menta:'heptagon',
  sanguine:'teardrop',   // blood drop — matches the medallion's sanguine BLOOD-TEARDROP frame (renderSigil); NOT a polygon, so typeBadge draws it as a path (shapePoints returns null → typeBadge special-cases 'teardrop')
  lair_ability:'octagram',   // 8-pointed star — a lair's grand/legendary domain action (distinct from the 5-point attack star + 6-point spell hexagram)
  trap_ability:'star4',      // 4-pointed caltrop star — a trap's sprung payload (spiky hazard; fewer points than the 5-point attack star)
  general_ability:'hexagon', // NOT an item_type — the HUD ability-island "General Abilities" CATEGORY chip (Spot Hidden, Search, Resolve, Snap Out of It, Stop Bleeding). Deliberately ability's hexagon: these ARE abilities, so the family should rhyme; the bronze fill + 'G' glyph are what separate them (owner 2026-09-02: on the plain 'ability' key it rendered the identical blue hexagon and read as the Abilities bucket)
  spell:'hexagram'};   // curse/menta get their own frame shapes (not the free-ability circle); spell = 6-pointed magic star (owner 2026-07-04: hexagon read as a clone of ability's hexagon — the hexagram is unmistakably "spell")
const TYPE_COLOR:Record<string,string>={full_ability:'#2dd4bf',ability:'#7aa2ff',attack_ability:'#ff7a7a',
  quick_ability:'#5fd28a',free_ability:'#8b93a7',reaction_ability:'#9333ea',
  opportunity_attack_ability:'#fbbf24',passive_ability:'#67e8f9',
  curse:'#d946ef',menta:'#818cf8',sanguine:'#e0245e',
  lair_ability:'#f97316',   // burnt orange — "territory / domain", unused elsewhere in the palette
  trap_ability:'#a3e635',   // hazard lime — "warning stripe", unused elsewhere in the palette
  general_ability:'#b08d57',// antique bronze — the ONLY warm neutral in the palette (no other browns), so the General-Abilities hexagon can never be mistaken for ability's #7aa2ff blue at 28px. "Base metal" also reads as the common/universal actions every creature has
  spell:'#818cf8'};   // full = teal, reaction = violet (pulled off the Ability blue cluster so Ability/Full/Reaction read distinct by colour, not just shape); curse = fuchsia, menta = indigo, sanguine = blood-red; spell = FAMILY_HUE.spell indigo (heptagon menta vs hexagon spell disambiguates)
const TYPE_AP:Record<string,number|string>={full_ability:3,attack_ability:1,quick_ability:1,reaction_ability:'r',
  opportunity_attack_ability:'r',free_ability:0,passive_ability:'p',ability:2,sanguine:0,
  lair_ability:1, // a lair fires ONE lair action per round (aps_total=1)
  trap_ability:0, // a trap has no AP at all — the armed Trapped status is its action economy
  general_ability:'g', // a CATEGORY, not a type — its members carry their own AP, so the slot shows a 'G' glyph instead of pips
  spell:2}; // sanguine ≈ free (costs charges); spell shows the default 2ap pips (per-item AP lives on the full medallion, not the type chip)
const TYPE_LABEL:Record<string,string>={full_ability:'Full Ability',attack_ability:'Attack Ability',quick_ability:'Quick Ability',
  reaction_ability:'Reaction',opportunity_attack_ability:'Opportunity Attack',free_ability:'Free Ability',
  passive_ability:'Passive',ability:'Ability',spell:'Spell',curse:'Curse',menta:'Menta',sanguine:'Sanguine',lair_ability:'Lair Ability',trap_ability:'Trap Ability',
  general_ability:'General Abilities'};
/* CARD ACCENT — one identity colour per item_type, for the .ft-rune-card hero band / medallion glow /
   eyebrow (options/tables/table_item_object_inc.php + css/style.css; stamped as --ft-rune-accent by
   window.ftMountRuneIcons). Sourced from the SAME palette as the medallion frame so the card system
   never drifts from the sigil. Unlike the frame, SPELLS collapse to a single hue (FAMILY_HUE.spell) so
   "this is a spell" reads at the card level regardless of circle; menta uses FAMILY_HUE.menta to stay
   distinct from spell's #818cf8. (user 2026-06-01) */
function typeAccent(ir:RuneIR):string{
  /* SPELLS use their Circle of Magic colour (per school: Evocation red, Abjuration blue, …) — same
     as the medallion frame, and it pulls spells out of the flat-indigo blue cluster. (user 2026-06-01) */
  if(ir.isSpell) return CIRCLE_COLOR[ir.circle ?? '']||FAMILY_HUE.spell||'#818cf8';
  if(ir.itype==='menta') return FAMILY_HUE.menta||'#a78bfa';
  /* full_ability now has its OWN distinct TYPE_COLOR (teal #2dd4bf), no longer shared with ability's
     blue — so the band reuses the frame colour directly (no special-case needed). The old #6366f1
     indigo override only existed to break the Ability/Full blue tie, which the new colour resolves. */
  return TYPE_COLOR[ir.itype]||FAMILY_HUE.ability||'#7aa2ff';}
/* Secondary accent — only spells differ: the SPHERE colour, blended into the band as a 2nd stop
   alongside the circle colour (so a spell card reads its sphere AND its circle). Everything else
   reuses the primary, giving a single-tone band. */
function typeAccent2(ir:RuneIR):string{
  if(ir.isSpell){const t=SPHERE_TEX[sphereKey(ir.sphere)??''];if(t&&t[0])return t[0];}
  return typeAccent(ir);}
const DMG:Record<string,{c:string;g:string}>={Fire:{c:'#FF0000',g:'flame'},Cold:{c:'#5D7984',g:'shard'},Lightning:{c:'#5A6998',g:'bolt'},
  Necrotic:{c:'#ADCEA1',g:'skull'},Radiant:{c:'#FFFF00',g:'sun'},Acid:{c:'#BAC475',g:'drop'},
  Poison:{c:'#BDCA78',g:'drop'},Psychic:{c:'#CBB3C7',g:'spiral'},Force:{c:'#864745',g:'ring'},
  Thunder:{c:'#746086',g:'bolt'},Bludgeoning:{c:'#FF007F',g:'shard'},Piercing:{c:'#B2B2B2',g:'bolt'},
  Slashing:{c:'#FF00FF',g:'shard'}};
const DMG_LC:Record<string,{c:string;g:string}>={};for(const k in DMG){const entry=DMG[k];if(entry)DMG_LC[k.toLowerCase()]=entry;}
// case-insensitive so "Cold"/"cold" both resolve to the real colour+glyph (else a name miss falls back
// to grey #9ca3af, which is light enough to flip the glyph ink to dark — looked like Cold "wasn't white").
const dmg=(n:unknown):{c:string;g:string}=>{const key=String(n??'');return DMG[key]||DMG_LC[key.toLowerCase()]||{c:'#9ca3af',g:'ring'};};
const vname=(n:string|null|undefined):string=>(n||'').toLowerCase().replace(/[^a-z0-9]+/g,'_');
/* Resolve UUIDs AND display names through the shared icon registry: class crests can have a
   historical filename that differs from their current label. Unregistered bespoke art stays name-based. */
const iconFile=(n:unknown):string=>{const s=String(n||''),key=vname(s);
  const host:RuneEngineWindow|null=typeof window!=='undefined'?(window as RuneEngineWindow):null;
  const raw=host?.FT_ICON_REGISTRY||host?.ICON_LOOKUP;
  const reg=raw!==null&&typeof raw==='object'&&!Array.isArray(raw)?raw as Record<string,IconRegistryEntry>:null;
  const e=reg&&(reg[s]||reg[s.toUpperCase()]||reg[s.toLowerCase()]||reg[key]);
  return e&&e.file?e.file:key;};
const AP_OF:Record<string,number>={'1ap':1,'2ap':2,'3ap':3};
/* spell frame SHAPE encodes casting-time AP, reusing the ability shape vocabulary so AP reads the
   same on spells and abilities: 1→diamond (quick), 2→hexagon (ability), 3→octagon (full).
   Special/long casts have no AP shape (circle frame) and show an emblem below; timer casts show a clock. */
const CAST_SHAPE:Record<string,string>={'1ap':'diamond','2ap':'hexagon','3ap':'octagon','reaction':'pentagon','free':'circle'};
/* the LONG/RITUAL casts — must stay in lockstep with the canonical Janus set (calc_pl_system.php:2929,
   quick_ritual_discount.php, every sanguine_cast_* gate) and with CastingTime::IDS, which maps ALL SIX
   to 3 AP. The three *_hours values were missing here until 2026-07-30: apCue() fell through to
   AP_OF[c] (undefined) and badged them "Free (no AP)" — the exact opposite of the truth.
   ⚠ Scope of the damage, precisely: that was LIVE-WRONG for 26 SPELLS (the only badged type at the
   time). The 5 curses on those casts drew no badge at all pre-2026-07-30, so extending the apVar
   gate WITHOUT this fix is what would have made them wrong — they were never wrong historically.
   Both now take the ⏲ treatment the other three timer casts already had. */
const TIMER_CASTS=new Set(['one_minute','ten_minutes','one_hour','eight_hours','twelve_hours','twenty_four_hours']);
const CAST_LABEL:Record<string,string>={'1ap':'1 AP','2ap':'2 AP','3ap':'3 AP','reaction':'Reaction','free':'Free (no AP)',
  'passive':'Passive','one_minute':'1 Minute','ten_minutes':'10 Minutes','one_hour':'1 Hour',
  'eight_hours':'8 Hours','twelve_hours':'12 Hours','twenty_four_hours':'24 Hours'};
const RANGE_TIER:Record<string,number>={Self:0,Touch:0,Reach:1,Close:2,Near:3,Far:4,'Very Far':5,Sight:6,Unlimited:6};
const DUR_RINGS:Record<string,number>={instant:0,seconds:1,rounds:1,minutes:2,hours:3,days:4};
/* distinct symbol per RollType (additions.php RollType::LIST):
   skill / spell_skill, the 4 attack rolls (weapon|spell × melee|ranged), healing / reduce-healing /
   heal-over-time, damage / reduce-damage / object-damage, damage-over-time, contesting. */
function rollGlyph(name:string|null|undefined):string{const n=(name||'').toLowerCase();
  const reduce=n.includes('reduce'), spell=n.includes('spell');
  if(n.includes('contest'))return 'contest';
  if(n.includes('attack')){const base=n.includes('ranged')?'atk_ranged':'atk_melee';return spell?base+'_spell':base;}
  if(n.includes('mana'))return n.includes('drain')?'mana_drain':(reduce?'mana_reduce':'mana');
  if(n.includes('luck'))return reduce?'luck_reduce':'luck';
  if(n.includes('essence'))return 'essence';
  if(n.includes('regen'))return 'regen';
  if(n.includes('vitality'))return 'vitality';
  if(n.includes('recharge'))return 'recharge';
  if(n.includes('heal')){if(n.includes('over time'))return 'heal_dot';return reduce?'heal_reduce':'heal';}
  if(n.includes('over time')||n.includes('dot'))return 'dot';   // damage over time
  if(n.includes('skill'))return spell?'skill_spell':'skill';
  if(n.includes('object'))return 'dmg_object';
  if(reduce)return 'dmg_reduce';
  return 'damage';}
/* family colour for NON-damage rolls so the core/dice read as life/mana/luck/essence, not grey "Force". */
function rollFamilyColor(rk:string):string{
  if(/heal|vitality|regen/.test(rk))return '#34d399';   // life = green
  if(/mana/.test(rk))return '#38bdf8';                   // mana = blue
  if(/luck/.test(rk))return '#fbbf24';                   // luck = gold
  if(/essence/.test(rk))return '#a78bfa';                // essence = violet
  if(/recharge/.test(rk))return '#22d3ee';              // recharge = cyan
  return '#cbd5e1';}                                     // skill/contest/other = slate

/* ===== readers + compiler ===== */
const lt=(a:AbilityArr):Record<string,unknown>=>a.item_layer_types||{};
/* UUID→item_name resolver for the bare-UUID reference layers (skill_roll_contester/contestee,
   consumes_ability_charges, add_charges_to_ability, …). The CDN icon files are keyed by the snake_cased
   item_name (vname()), so a raw UUID slug 404s — resolving the UUID to its name here is what makes the
   satellite icons AND their tooltips land. _RUNE_CAT is the ability arr's `catalog` (UUID→{item_name,
   item_type}); the mount path (index.ts render() / the footer ftMountRuneIcons scanner) stamps it via
   setRuneCatalog() BEFORE compile() runs. Catalog keys are the capitalized UUID, same as the refs, so a
   direct lookup hits with no case-juggling. Empty catalog (e.g. the admin lab) → unchanged passthrough. */
let _RUNE_CAT:Record<string,RuneCatalogEntry>={};
/* MERGE (union), don't replace: a mount stamps BOTH the caster ROOT catalog (renderPcSheetPane) AND the
   ability's OWN per-ability catalog (shipped in its blob) — the latter holds cross-class charge/contest
   refs (e.g. a Deathbringer ability that consumes a Wizard "Mastery" charge) that the root catalog omits.
   UUIDs are globally unique and names are static, so accumulating entries across a session is always
   sound — never wrong, just additive. Pass a falsy/empty catalog → harmless no-op. */
function setRuneCatalog(c:unknown):void{if(c&&typeof c==='object'&&!Array.isArray(c)){const cat=c as Record<string,unknown>;for(const k in cat){const value=cat[k];if(value&&typeof value==='object'&&!Array.isArray(value)&&Object.prototype.hasOwnProperty.call(cat,k))_RUNE_CAT[k]=value as RuneCatalogEntry;}}}
/* resolve a UUID against the stamped catalog, tolerating BOTH entry shapes: the FLAT per-ability
   {item_name,item_type} (the PHP card blob's `catalog`) and the NESTED full-item {items:{item_name}}
   (the caster ROOT `catalog`, 400+ entries, which is what the PC sheet stamps). Catalog keys + the
   layer refs are both upper-case UUIDs; the toUpperCase() fallback guards any case drift. */
const _UUID_RX=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function catName(id:unknown):string|null{if(typeof id!=='string')return null;let e=_RUNE_CAT[id];if(!e)e=_RUNE_CAT[id.toUpperCase()];
  if(e){
    if(typeof e.item_name==='string')return e.item_name;
    if(e.items&&typeof e.items.item_name==='string')return e.items.item_name;
  }
  /* path-independent fallback: any render surface (object card, info panel, encounter bar, …) that didn't
     stamp a local catalog still resolves via the session-global FateTide cache walk (core-helpers.js
     ftItemNameById). Gated to UUID-shaped strings so plain names/damage types skip the lookup. */
  if(typeof id==='string'&&_UUID_RX.test(id)){
    const r=(typeof window!=='undefined')&&(window as RuneEngineWindow).ftItemNameById;
    if(typeof r==='function'){const nm=r(id);if(typeof nm==='string'&&nm!=='')return nm;}
  }
  return null;}
function name1(v:unknown):string|null{if(v==null)return null;
  if(typeof v==='string')return catName(v)||v;
  if(Array.isArray(v))return v.length?name1(v[0]):null;
  if(typeof v==='object'&&'items' in v){const items=(v as RuneItemRef).items;return typeof items?.item_name==='string'?items.item_name:null;}
  return null;}
function names(v:unknown):string[]{if(v==null)return[];if(Array.isArray(v))return v.map(name1).filter((name):name is string=>name!==null);
  const n=name1(v);return n?[n]:[];}
/* parse any status layer key → {mode:applied|removed, who:target|caster, timing} */
function parseStatusKey(k:string):{mode:string;who:string;timing:string}|null{
  const m=k.match(/^status_effect_(applied_to|removed_from)_(target|caster|self)_(.+)$/);
  if(!m)return null;
  const mode=m[1],who=m[2],timing=m[3];if(!mode||!who||!timing)return null;
  return {mode:mode==='applied_to'?'applied':'removed',
          who:who==='self'?'caster':who,
          timing:timing.replace(/_2$/,'').replace(/_/g,' ')};}
const isStatusConditionKey=(key:string):boolean=>/^if_(target|caster)_has_status_effect/.test(key);
/** Saved catalog rune views nest exactly the references consumed as statuses here. */
export function isRuneStatusLayer(key:string):boolean{return parseStatusKey(key)!==null||isStatusConditionKey(key);}
const TRIGGER_KEYS=['triggers_on_roll_type','triggers_on_roll_type_against',
  'triggers_on_roll_type_against_the_caster',
  'triggers_on_next_roll_type','triggers_on_next_roll_type_against']; // user: these should still show the bolt
/* EVENT triggers — boolean "1" flags from the master ability form's trigger group (base_ability_form_inc.php).
   The lightning bolt = "this is a triggered/reactive ability"; the specific condition(s) live in the hover.
   (The one-off triggers_on_next_roll_type* are modifier mechanics, intentionally excluded —
   see [[feedback_ability_button_trigger_detection]].) */
const EVENT_TRIGGERS:Array<[string,string]>=[
  ['triggers_on_kill','On kill'],
  ['triggers_on_hit','On hit'],
  ['triggers_on_miss','On miss'],
  ['triggers_on_critical_hit','On critical hit'],
  ['triggers_on_not_a_critical_hit','On non-critical hit'],
  ['triggers_on_move','On move'],
  ['triggers_on_unlucky_roll','On unlucky roll'],
  ['triggers_on_caster_summon_death','On summon death'],
  ['triggers_on_self_zero_vitality','On dropping to 0 Vitality'],
  ['triggers_on_entered_aoe','On entering an area'],
  ['triggers_on_attack_roll_against','When attacked'],
];
function compile(spell:AbilityArr):RuneIR{const L=lt(spell);
  // structured status entries: {it, who, mode, timing}
  const statusEntries:RuneStatusEntry[]=[];
  for(const k of Object.keys(L)){const meta=parseStatusKey(k);if(!meta)continue;
    const arr=Array.isArray(L[k])?L[k]:[];
    for(const it of arr){if(it&&typeof it==='object'&&'items' in it)statusEntries.push({it:it as RuneItemRef,...meta});}}
  const statusObjs=statusEntries.map(e=>e.it); // legacy flat list
  const trigNames=TRIGGER_KEYS.flatMap(k=>names(L[k]));
  /* event-trigger labels present on this ability (boolean flags) */
  const eventTrigLabels=EVENT_TRIGGERS.filter(([k])=>L[k]=='1').map(([,label])=>label);
  const dice=L.dice?parseInt(String(L.dice),10):0, die=L.die?parseInt(String(L.die),10):0;
  /* mod may be a flat number OR a DYNAMIC token like [total_level]/[body] (computed at runtime). */
  const modRaw=(L.mod!=null&&L.mod!=='')?String(L.mod):'';
  const mod=/^[+-]?\d+$/.test(modRaw)?parseInt(modRaw,10):0;
  const dynMod=modRaw!==''&&mod===0&&!/^[+-]?0$/.test(modRaw); // non-numeric mod = dynamic
  const itype=(spell.items&&spell.items.item_type)||'spell', isSpell=itype==='spell';
  let ap='2ap';
  const castingTime=name1(L.spell_casting_time);
  if(isSpell)ap=castingTime||'2ap';
  /* menta AND curse encode AP like spells (both carry spell_casting_time). Curse was missing here,
     so every curse fell through to the TYPE_AP lookup below — which has no 'curse' entry — and
     compiled as cast:'passive'. ability_button_renderer.php:70 has always read curses via
     spell_casting_time (`in_array($item_type,['spell','curse'])`), so the sigil disagreed with the
     button's own AP gate. Fixed 2026-07-24 while adding the AP badge, which surfaced it. */
  else if((itype==='menta'||itype==='curse')&&castingTime)ap=castingTime;
  else{const t=TYPE_AP[itype];ap=(t==='r'?'reaction':t==='p'?'passive':t===0?'free':t==null?'passive':`${t}ap`);}
  return{
  name:(spell.items&&spell.items.item_name)||'(unnamed)', id:(spell.items&&spell.items.item_id)||null, itype, isSpell,
  sphere:name1(L.spell_sphere),sphereAll:names(L.spell_sphere),circle:name1(L.spell_circle),
  /* MENTA manifestation (like circle_of_magic): menta_mental_manifestation or user_choice_N_menta */
  manifestation:(()=>{let m=name1(L.menta_mental_manifestation);
    if(!m){const k=Object.keys(L).find(k=>/^user_choice_\d+_menta$/.test(k));if(k)m=name1(L[k]);}return m;})(),
  /* contesting roll success → half damage (rendered as a ½ slashing the roll emblem up top) */
  halfOnContest:L.contesting_roll_successful_for_half_damage=='1',
  /* ADV/DIS on contesting rolls vs SPELLS — a serious defensive trait (≈ magic resistance);
     rendered as a prominent spell-ward SHIELD, not a tiny generic token. */
  spellContest:(L.contesting_roll_adv_vs_spells=='1'?'adv':(L.contesting_roll_dis_vs_spells=='1'?'dis':null)),
  /* ADV/DIS on contesting rolls vs specific STATUS EFFECTS — show each status's icon in a thin
     blue(adv)/red(dis) ring + a contest mark (distinct from the regular status satellites). */
  contestVsStatus:(()=>{const out:Array<{name:string;adv:boolean}>=[];
    names(L.contesting_roll_adv_vs_status_effect).forEach(n=>out.push({name:n,adv:true}));
    names(L.contesting_roll_dis_vs_status_effect).forEach(n=>out.push({name:n,adv:false}));
    return out;})(),
  /* IMMUNE to status effects — show each status's icon in a shiny-chrome ring (immunity cue). */
  immuneStatus:names(L.immune_status_effect),
  /* ROLL TYPE ADV/DIS (caster's rolls) + AGAINST (rolls made against the target) — show the roll-type
     glyph, ring blue=ADV / red=DIS; "against" gets a small pip. */
  rollAdvDis:(()=>{const out:Array<{rt:string;adv:boolean;against:boolean}>=[];
    names(L.roll_type_adv).forEach(n=>out.push({rt:n,adv:true,against:false}));
    names(L.roll_type_dis).forEach(n=>out.push({rt:n,adv:false,against:false}));
    names(L.roll_type_against_adv).forEach(n=>out.push({rt:n,adv:true,against:true}));
    names(L.roll_type_against_dis).forEach(n=>out.push({rt:n,adv:false,against:true}));
    return out;})(),
  /* "AGAINST" ability — has layers that act on the TARGET's rolls/actions (roll_type_against_*,
     triggers_*_against, *_item_type_against). Signalled by a → arrow across the top. Excludes the
     works_only/does_not_work "against" SCOPING layers (those are restrictions, not against-mechanics). */
  hasAgainst:Object.keys(L).some(k=>k.includes('against')&&!k.includes('does_not_work')&&!k.includes('works_only')&&!k.includes('only_works')),
  cast:ap, roll:name1(L.roll_type),
  damage:names(L.damage_type),dice,die,mod,dynMod,modRaw,
  /* TOTAL roll amount = dice average + flat mod (e.g. a flat +1000 Healing Roll with no dice).
     null when the only contribution is a dynamic mod (can't compute → shown as wavy lines, not a number). */
  avg:(dice&&die)?Math.round(dice*(die+1)/2)+mod:(mod||null),
  aoe:name1(L.aoe_type),aoeDist:L.aoe_distance?parseInt(String(L.aoe_distance),10):null,
  range:name1(L.range),
  durType:String(L.duration_type||'instant'),durInt:parseInt(String(L.duration_int??'0'),10)||0,
  concentration:L.spell_concentration=='1',ritual:L.spell_ritual=='1',
  sameTeam:L.effect_only_affects_same_team_type=='1',
  oppTeam:L.effect_only_affects_opposing_team_type=='1',
  persistentAoe:L.persistent_aoe=='1'||L.aoe_follows_target=='1',
  /* AURA = self-range sustained AoE that follows the caster (Janus: persistent_aoe + aoe_follows_target
     + range Self). Combined into one "aura" signal at render time. */
  isAura:(L.aoe_follows_target=='1' && L.persistent_aoe=='1' && name1(L.range)==='Self' && !!name1(L.aoe_type)),
  /* speed-change-remaining layers: {kind,up,val} — rune shows direction, not the number. */
  speedChanges:(()=>{const out:Array<{kind:string;up:boolean;val:string}>=[];['walking','flying','swimming','climbing','jumping','hovering','burrowing'].forEach(k=>{
    const v=L['change_speed_'+k+'_remaining']; if(v==null||v==='')return;
    out.push({kind:k,up:!(parseInt(String(v).replace('+',''),10)<0),val:String(v)});});return out;})(),
  /* BASE speed GRANTS: speed_flying/climbing/swimming/etc (grants a MODE of movement, e.g. flight 60) —
     distinct from change_speed_*_remaining (modifies remaining movement this turn). {kind,val}. */
  speedGrants:(()=>{const out:Array<{kind:string;val:string}>=[];['walking','flying','swimming','climbing','burrowing','hovering','jumping'].forEach(k=>{
    const v=L['speed_'+k]; if(v==null||v==='')return;
    out.push({kind:k,val:String(v)});});return out;})(),
  /* SHAPESHIFT: user_choice_N_shapeshift = creature_model choices the caster can morph into. Names only. */
  shapeshift:(()=>{const out:string[]=[];Object.keys(L).forEach(k=>{if(/^user_choice_\d+_shapeshift$/.test(k))out.push(...names(L[k]));});return out;})(),
  /* LEVEL (0–10): spells/curses use spell_level, menta uses menta_level. Shown as the bottom-center
     star/dot plaque for spell + menta + curse (the frame SHAPE — incl. the curse down-triangle — is untouched). */
  level:(()=>{const raw=(itype==='menta')?L.menta_level:L.spell_level;const n=parseInt(String(raw),10);
    return (raw!=null&&raw!==''&&Number.isFinite(n))?n:null;})(),
  hasTrigger:trigNames.length>0||eventTrigLabels.length>0,
  trigLabel:[...trigNames, ...eventTrigLabels].filter(Boolean).join(', '),
  contesterSkills:names(L.skill_roll_contester),   // caster's contest skill(s)
  contesteeSkills:names(L.skill_roll_contestee),   // target's contest skill(s)
  disarmed:L.disarmed=='1',
  /* CLASS / SUBCLASS gate — requires_class / requires_subclass mark this as a class- or subclass-
     granted ability (helps tell class vs subclass spells/abilities apart). Each is a list of
     class/subclass item names. requires_class_level = the min class level (hover only — kept off the
     glyph to avoid clutter; stored array-or-scalar, so unwrap the first entry). */
  reqClass:names(L.requires_class), reqSubclass:names(L.requires_subclass),
  reqClassLevel:(()=>{let v=L.requires_class_level;if(Array.isArray(v))v=v[0];const n=parseInt(String(v),10);return Number.isFinite(n)?n:null;})(),
  /* defenses — each is a list of damage types (same shape as damage_type) */
  resist:names(L.damage_type_resistance), immune:names(L.damage_type_immunity),
  vuln:names(L.damage_type_vulnerability),
  /* AP change during turn: a signed number, or 'r' = reaction reset. The dedicated `reaction_reset`
     boolean flag maps to the same 'r' emblem (refresh-arrow disc). */
  apChange:(L.ap_change_during_turn!=null&&L.ap_change_during_turn!=='')?String(L.ap_change_during_turn)
          :(L.reaction_reset=='1'?'r':null),
  /* granted/attached abilities — an ability layer (reaction_ability etc.) nested as an array of items */
  grantedAbilities:(()=>{const ATK=['full_ability','attack_ability','quick_ability','free_ability','reaction_ability','opportunity_attack_ability','ability','passive_ability'];
    const out:Array<{name:string;type:string}>=[];ATK.forEach(k=>{if(Array.isArray(L[k]))names(L[k]).forEach(n=>out.push({name:n,type:k}));});return out;})(),
  fixedDc:L.ability_dc?parseInt(String(L.ability_dc),10):null,
  moveToward:L.caster_moves_towards_target_in_ft?parseInt(String(L.caster_moves_towards_target_in_ft),10):null,
  targetRetries:L.target_retries_contesting_roll_on_end_of_turn=='1',
  /* skill roll advantage / disadvantage (arrays of which skills, or a flag) */
  skillAdv:(Array.isArray(L.skill_roll_adv)&&L.skill_roll_adv.length>0)||L.skill_roll_adv=='1',
  skillDis:(Array.isArray(L.skill_roll_dis)&&L.skill_roll_dis.length>0)||L.skill_roll_dis=='1',
  skillAdvNames:names(L.skill_roll_adv), skillDisNames:names(L.skill_roll_dis),
  resetsLong:L.ability_resets_long_rest=='1', resetsShort:L.ability_resets_short_rest=='1',
  rechargeBegin:L.recharge_range_begin?parseInt(String(L.recharge_range_begin),10):null,
  rechargeEnd:L.recharge_range_end?parseInt(String(L.recharge_range_end),10):null,
  /* charge POOL size (ties to the long-rest/recharge disc): dots shown near the moon only when >1. */
  charges:(()=>{const v=L.ability_activation_charges??L.ability_charges;const n=parseInt(String(v),10);return Number.isFinite(n)?n:null;})(),
  /* teleport-remaining change — rendered in the SPEED area (distinct purple portal glyph). */
  teleportChange:(L.change_teleport_remaining!=null&&L.change_teleport_remaining!=='')?String(L.change_teleport_remaining):null,
  /* charges: consume (red) spends another ability's charges; add (green) grants charges (thief etc.) */
  consumesCharges:(Array.isArray(L.consumes_ability_charges)&&L.consumes_ability_charges.length)?
    (L.consumes_charges_qty?parseInt(String(L.consumes_charges_qty),10):1):null,
  addsCharges:(Array.isArray(L.add_charges_to_ability)&&L.add_charges_to_ability.length)?
    (L.add_charges_qty?parseInt(String(L.add_charges_qty),10):1):null,
  consumesName:names(L.consumes_ability_charges)[0]||null,
  addsName:names(L.add_charges_to_ability)[0]||null,
  /* light: creates illumination (or dim-only); destroys light. simple candle/sun emblem, no exact ft. */
  light:(L.create_light_radius||L.bright_light_dim_light||L.create_dim_light_only=='1')
    ?(L.create_dim_light_only=='1'?'dim':'bright'):null,
  destroysLight:L.effect_can_destroy_light=='1',
  /* FALL PROTECTION: negate_falling_damage_in_feet — symbol only (a soft-landing/feather mark),
     no integer/variable shown per design (it's an icon, the amount lives in hover). */
  fallNegate:(L.negate_falling_damage_in_feet!=null&&L.negate_falling_damage_in_feet!=='')?String(L.negate_falling_damage_in_feet):null,
  /* LIMITED VISIBILITY: limited_visibility_ft — rare; a small fog/obscured mark, value in hover only. */
  limitedVis:(L.limited_visibility_ft!=null&&L.limited_visibility_ft!=='')?String(L.limited_visibility_ft):null,
  /* CONDITION gate: "if target/caster has (or lacks) a status" — capture the status entries so we
     can show the real status ICON (purple-ringed); label kept for hover. */
  conditionStatuses:(()=>{const ks=Object.keys(L).filter(isStatusConditionKey);
    const out:Array<{name:string;who:string;not:boolean}>=[];
    ks.forEach(k=>{const not=k.includes('not_active');const who=k.includes('target')?'target':'caster';
      names(L[k]).forEach(n=>out.push({name:n,who,not}));});
    return out;})(),
  condition:(()=>{const ks=Object.keys(L).filter(isStatusConditionKey);
    if(!ks.length)return null;
    const labels=ks.flatMap(k=>{const not=k.includes('not_active');const who=k.includes('target')?'target':'caster';
      return names(L[k]).map(n=>`${who} ${not?'lacks':'has'} ${n}`);});
    return labels.length?labels.join('; '):'conditional';})(),
  /* ROLL MANIPULATION: doubled/halved/maximized/min/rerolled etc. — generic flag, just "something here". */
  rollManip:(()=>{const ks=Object.keys(L).filter(k=>
      /^roll_type_roll_result_is_|result_is_(doubled|halved|tripled|maximized|minimized)|roll_type_is_rolled_at_max|_rerolls?_/.test(k)&&L[k]);
    return ks.length?ks.map(k=>k.replace(/_/g,' ')).join('; '):null;})(),
  shortCaster:(L.effect_ends_on_start_of_caster_next_turn=='1'||L.effect_ends_on_end_of_caster_next_turn=='1'),
  shortTarget:(L.effect_ends_on_target_start_of_turn=='1'||L.effect_ends_on_target_end_of_turn=='1'),
  /* SUMMON: the `summon` layer PLUS user_choice_<N>_summon (choose-your-summon) — same icon logic. */
  summon:(()=>{const a=[];if(Array.isArray(L.summon))a.push(...L.summon);
    Object.keys(L).forEach(k=>{if(/^user_choice_\d+_summon$/.test(k)&&Array.isArray(L[k]))a.push(...L[k]);});
    return a.map(name1).filter((name):name is string=>name!==null);})(),
  fabricate:names(L.fabricate),
  /* core thumbnail: first picture across summon / user_choice summon / fabricate, if any attached */
  coreImg:(()=>{const a=[];if(Array.isArray(L.summon))a.push(...L.summon);
    Object.keys(L).forEach(k=>{if(/^user_choice_\d+_summon$/.test(k)&&Array.isArray(L[k]))a.push(...L[k]);});
    if(Array.isArray(L.fabricate))a.push(...L.fabricate);
    const p=a.find(x=>x&&x.picture_url);return p?p.picture_url:null;})(),
  _rawKeys:Object.keys(L),
  _statusKeys:Object.keys(L).filter(k=>k.indexOf('status_effect')===0),
  statusObjs, statusEntries};}
const ROMAN=['','I','II','III','IV','V','VI','VII','VIII','IX','X'];
const roman=(n:number):string=>ROMAN[n]||String(n);
function compileStatus(st:AbilityArr):StatusRuneIR{const L=lt(st),dn=names(L.damage_type);
  const full=st.items?.item_name||'';
  // strip ANY trailing parenthetical so the CDN icon resolves: "Shielded (4)"→"Shielded",
  // "Bleeding (3d6)"→"Bleeding" (was only stripping pure-digit parens, so dice-suffix names 404'd).
  // strip a trailing "(N)" OR a trailing " N" / ", N" (catalog uses "Judgement 1" with a SPACE,
  // DB items use "Judgement, 1" with a COMMA, others use "(N)") so the BASE resolves; keep the count.
  const base=full.replace(/\s*\([^)]*\)\s*$/,'').replace(/[\s,]+\d+$/,'').trim();
  const stacks=(full.match(/\((\d+)\)\s*$/)||full.match(/[\s,]+(\d+)$/)||[])[1]; // numeric stack/level, if present
  const death=DEATH_RX.test(base);   // dying/dead/unconscious/maimed → death iconography
  /* authoritative catalog meta. Try the FULL name normalised (comma→space: "Judgement, 1"→"judgement 1")
     for a LEVELED icon first, then the stripped base. icon uses variable_name (handles 'challanged' AND
     leveled 'judgement_1'); boon from status_type. */
  const fullNorm=full.replace(/,/g,' ').replace(/\s+/g,' ').trim().toLowerCase();
  const meta=STATUS_META[fullNorm]||STATUS_META[base.toLowerCase()]||null;
  const icon=(meta&&meta.v)?meta.v:base;
  // Boon/bane is AUTHORITATIVE from the catalog: STATUS_META[*].b (StatusEffect status_type, emitted
  // by emit_rune_data_global_inc.php). The name heuristic below is consulted ONLY when the status is
  // uncatalogued (no meta entry) — e.g. a runtime status not in StatusEffect::LIST.
  const boon=!death&&(meta?meta.b===1:(BOON_STATUSES.has(vname(base))||BOON_RX.test(base)));
  const speedish=L.speed_walking||L.speed_climbing||L.speed_flying||L.ap_change_at_start_of_turn;
  /* death statuses get a skull motif (the real v-status icon shows via artNode/CDN when present); else damage/boon/speed/ring. */
  const kind=death?'skull':(dn.length?dmg(dn[0]).g:(boon?'sun':(speedish?'shard':'ring')));
  const col=death?'#cbd5e1':(dn.length?dmg(dn[0]).c:(boon?'#5fd28a':'#ef5a5a'));
  const persistent=L.status_effect_permanent=='1'||L.duration_type==='permanent';
  return{name:full,icon,stacks:stacks?parseInt(stacks,10):null,motif:kind,color:col,boon,persistent};}
/* LAST-DITCH boon detector — consulted ONLY for statuses absent from STATUS_META (uncatalogued
   runtime statuses). The catalog's status_type, shipped as STATUS_META[*].b by
   emit_rune_data_global_inc.php, is authoritative and is preferred at the call site above; a
   catalogued status never reaches these. Kept purely as a safety net for un-exported names. */
const BOON_STATUSES=new Set(['hasted','ageless','blessed','regenerating','invisible','flying']);
const BOON_RX=/shield|ward|bless|empower|haste|regenerat|inspir|protect|fortif|invisib|bolster|aegis|hardened|braced|sanctuar|guard|resist|enrage|rally|haven/i;
/* death/dying family (special v-status statuses) — skull motif + grey, reads as a death effect. */
const DEATH_RX=/^(dying|dead|deceased|unconscious|maimed|petrified|out of breath)\b/i;
const durLabel=(ir:RuneIR):string=>ir.durType==='instant'?'Instantaneous'
  :`${ir.durInt||''} ${ir.durType}${ir.concentration?' · concentration':''}`.trim();

/* ===== SVG helpers ===== */
const NS='http://www.w3.org/2000/svg';
const el=(t:string,a:SvgAttrs={}):SVGElement=>{const e=document.createElementNS(NS,t);for(const k in a)e.setAttribute(k,a[k] as string);return e;};
const P=(cx:number,cy:number,r:number,deg:number):Point=>[cx+r*Math.cos(deg*Math.PI/180),cy+r*Math.sin(deg*Math.PI/180)];
const svgEl=(size:number):SVGElement=>el('svg',{width:size,height:size,viewBox:'0 0 100 100'});
function part(parent:SVGElement,tag:string,label:string):SVGElement{const g=el('g');g.setAttribute('data-part',label);g.setAttribute('data-tag',tag);parent.appendChild(g);return g;}
let _gid=0;
function texGradient(defs:SVGElement,stops:string[]):string{const id='tx'+(_gid++);
  const lg=el('linearGradient',{id,x1:'0%',y1:'0%',x2:'100%',y2:'100%'});
  stops.forEach((c:string,i:number)=>lg.appendChild(el('stop',{offset:(i/(stops.length-1)*100)+'%','stop-color':c})));
  defs.appendChild(lg);return `url(#${id})`;}
function shapePoints(cx:number,cy:number,r:number,shape:string):Point[]|null{
  const poly=(sides:number,rot=0):Point[]=>{const p:Point[]=[];for(let i=0;i<sides;i++){const[x,y]=P(cx,cy,r,-90+rot+i*360/sides);p.push([x,y]);}return p;};
  switch(shape){
    case 'triangle':return poly(3);
    case 'square':return poly(4,45);
    case 'diamond':return poly(4,0);
    case 'pentagon':return poly(5);
    case 'hexagon':return poly(6);
    case 'octagon':return poly(8,22.5);
    case 'triangle_down':return poly(3,180);   // inverted triangle — curse (ominous)
    case 'heptagon':return poly(7);             // 7-sided — menta (psionic)
    case 'chevron':return [P(cx,cy,r,-90),P(cx,cy,r,-20),P(cx,cy,r*0.5,90),P(cx,cy,r,200)];
    case 'star':{const p:Point[]=[];for(let i=0;i<10;i++){const rr=i%2?r*0.5:r;p.push(P(cx,cy,rr,-90+i*36));}return p;}
    case 'hexagram':{const p:Point[]=[];for(let i=0;i<12;i++){const rr=i%2?r*0.42:r;p.push(P(cx,cy,rr,-90+i*30));}return p;} // 6-pointed magic star — spell (distinct from the 5-point attack star and the plain ability hexagon)
    case 'octagram':{const p:Point[]=[];for(let i=0;i<16;i++){const rr=i%2?r*0.5:r;p.push(P(cx,cy,rr,-90+i*22.5));}return p;} // 8-pointed star — lair_ability (grander than the 6-point spell hexagram)
    case 'star4':{const p:Point[]=[];for(let i=0;i<8;i++){const rr=i%2?r*0.38:r;p.push(P(cx,cy,rr,-90+i*45));}return p;} // 4-pointed caltrop star — trap_ability (deep concave spikes read "hazard")
    default:return null;
  }}
function drawFrame(g:SVGElement,cx:number,cy:number,r:number,shape:string,stroke:string,sw:number):void{
  const pts=shapePoints(cx,cy,r,shape);
  if(!pts){g.appendChild(el('circle',{cx,cy,r,fill:'none',stroke,'stroke-width':sw,'stroke-linejoin':'round'}));return;}
  g.appendChild(el('polygon',{points:pts.map(p=>p[0]+','+p[1]).join(' '),fill:'none',stroke,'stroke-width':sw,'stroke-linejoin':'round'}));}
function artNode(g:SVGElement,name:string|null|undefined,motifKind:string,x:number,y:number,r:number,color:string,useCdn:boolean,noFallback?:boolean):void{
  if(useCdn&&name){
    /* fallback motif UNDER the image: if the CDN png 404s (onerror removes the <image>), the motif
       remains so the disc is never empty (fixes "missing icon" for charges / skill / summon).
       noFallback = STATUS satellites: the disc border + icon are enough — NO shape behind the icon. */
    if(!noFallback&&motifKind&&motifKind!=='ring')g.appendChild(motifNode(motifKind,x,y,r,color,false));
    const img=el('image',{x:x-r,y:y-r,width:2*r,height:2*r,
      href:ICON_CDN+iconFile(name)+'.png','preserveAspectRatio':'xMidYMid meet'});
    img.setAttribute('onerror','this.remove()');g.appendChild(img);}
  else g.appendChild(motifNode(motifKind,x,y,r,color,true));}
function pieDisc(g:SVGElement,cx:number,cy:number,r:number,colors:string[]):void{
  if(colors.length<=1){g.appendChild(el('circle',{cx,cy,r,fill:colors[0]||'#555'}));return;}
  const n=colors.length;
  for(let i=0;i<n;i++){const a0=-90+i*360/n,a1=-90+(i+1)*360/n;
    const[x0,y0]=P(cx,cy,r,a0),[x1,y1]=P(cx,cy,r,a1);
    g.appendChild(el('path',{d:`M${cx} ${cy} L${x0} ${y0} A${r} ${r} 0 0 1 ${x1} ${y1} Z`,fill:colors[i]||colors[0]||'#64748b'}));}}
/* ink that contrasts with a fill colour — PERCEPTUAL luminance (HSL lightness misjudges yellow:
   Radiant #FFFF00 reads bright but L=0.5). Dark glyph on bright fills, white glyph on dark fills. */
function glyphInk(hex:string):string{const h=hex.replace('#','');
  const r=parseInt(h.slice(0,2),16),g=parseInt(h.slice(2,4),16),b=parseInt(h.slice(4,6),16);
  return (0.299*r+0.587*g+0.114*b)>150?'#0c0f17':'#ffffff';}
/* DISTINCT glyph per damage type — a redundant shape channel over the colour (colour-blind backstop,
   and disambiguates near-identical colours like Bludgeoning vs Slashing). Drawn on SINGLE-type cores. */
function dmgGlyph(g:SVGElement,x:number,y:number,r:number,type:string,ink:string):void{
  const o={stroke:ink,'stroke-width':1.4,fill:'none','stroke-linecap':'round','stroke-linejoin':'round'};
  const F={fill:ink,stroke:'none'}, P2=(d:string,a?:SvgAttrs)=>g.appendChild(el('path',{d,...(a||o)})), C=(cx2:number,cy2:number,rr:number,a?:SvgAttrs)=>g.appendChild(el('circle',{cx:cx2,cy:cy2,r:rr,...(a||o)}));
  switch(type){
    case 'Fire': P2(`M${x} ${y-r} q ${r*0.8} ${r*0.75} 0 ${r*1.7} q ${-r*0.8} ${-r*0.6} 0 ${-r*1.7} Z`,F);break;
    case 'Cold': for(let i=0;i<3;i++){const a=i*60*Math.PI/180;P2(`M${x-Math.cos(a)*r} ${y-Math.sin(a)*r} L${x+Math.cos(a)*r} ${y+Math.sin(a)*r}`);}break; // 6-spoke ice
    case 'Lightning': P2(`M${x+r*0.4} ${y-r} L${x-r*0.45} ${y+r*0.1} L${x+r*0.1} ${y+r*0.1} L${x-r*0.4} ${y+r} L${x+r*0.55} ${y-r*0.2} L${x} ${y-r*0.2} Z`,F);break;
    case 'Necrotic': C(x,y-r*0.12,r*0.72); C(x-r*0.28,y-r*0.18,r*0.15,F); C(x+r*0.28,y-r*0.18,r*0.15,F); P2(`M${x-r*0.3} ${y+r*0.5} H${x+r*0.3}`);break; // skull
    case 'Radiant': C(x,y,r*0.42); for(let i=0;i<8;i++){const a=i*45*Math.PI/180;P2(`M${x+Math.cos(a)*r*0.62} ${y+Math.sin(a)*r*0.62} L${x+Math.cos(a)*r} ${y+Math.sin(a)*r}`);}break; // sun
    case 'Acid': P2(`M${x} ${y-r} q ${r*0.75} ${r} 0 ${r*1.55} q ${-r*0.75} ${-r*0.55} 0 ${-r*1.55} Z`,F); C(x,y+r*0.95,r*0.18,F);break; // drop + drip
    case 'Poison': for(let i=0;i<3;i++){const a=(i*120-90)*Math.PI/180;C(x+Math.cos(a)*r*0.48,y+Math.sin(a)*r*0.48,r*0.34);}break; // trefoil
    case 'Psychic':{let d=`M${x} ${y}`;for(let t=0;t<7;t+=0.35){const rr=r*t/7;d+=` L${x+rr*Math.cos(t)} ${y+rr*Math.sin(t)}`;}P2(d);break;} // spiral
    case 'Force': for(let i=0;i<4;i++){const a=i*90*Math.PI/180;P2(`M${x} ${y} L${x+Math.cos(a)*r} ${y+Math.sin(a)*r}`,{...o,'stroke-width':1.7});} for(let i=0;i<4;i++){const a=(i*90+45)*Math.PI/180;P2(`M${x} ${y} L${x+Math.cos(a)*r*0.5} ${y+Math.sin(a)*r*0.5}`);}break; // burst
    case 'Thunder': for(let i=1;i<=3;i++){const rr=r*0.3*i;P2(`M${x-r*0.2} ${y-rr} A${rr} ${rr} 0 0 1 ${x-r*0.2} ${y+rr}`);}break; // soundwaves
    case 'Bludgeoning': P2(`M${x} ${y-r*0.15} V${y+r}`); g.appendChild(el('rect',{x:x-r*0.72,y:y-r,width:r*1.44,height:r*0.55,rx:r*0.12,...o}));break; // hammer
    case 'Piercing': P2(`M${x} ${y+r} V${y-r*0.35}`); P2(`M${x} ${y-r} L${x-r*0.45} ${y-r*0.3} L${x+r*0.45} ${y-r*0.3} Z`,F);break; // spike
    case 'Slashing': // SWORD (coherent with Bludgeoning=hammer / Piercing=spike): diagonal blade + crossguard + pommel
      P2(`M${x-r*0.55} ${y+r*0.55} L${x+r*0.82} ${y-r*0.82}`,{...o,'stroke-width':1.7});
      P2(`M${x-r*0.9} ${y+r*0.3} L${x-r*0.3} ${y+r*0.9}`,{...o,'stroke-width':1.4});
      C(x-r*0.72,y+r*0.72,r*0.17,F);break;
    default: C(x,y,r*0.5,F); // unknown type → simple dot
  }}

/* ===== SIGIL renderer (ported from rune-forge POC v13) ===== */
/* min-LOD per element (by part() data-tag). 0 = identity (always shown, even on tiny buttons);
   2 = full detail only (the generic-token arc); everything else defaults to 1 (standard+). */
const LABEL_LOD:Record<string,number>={
  Sphere:0,'Circle of Magic':0,Manifestation:0,'Action Cost':0,'Ability Type':0,
  Core:0,'Roll Type':0,Trigger:0,Against:0,Status:0,
  Range:0,Area:0,Dice:0,   // Compact-keep (user): range, AoE badge (size-dots gated to lod≥1), damage ticks
  Modifier:2,More:2};
/* ===== AP COST BADGE (placement variants — opt.apBadge) ==================================
   PROBLEM this exists to fix: the action-point cost is never drawn explicitly, only INFERRED.
   On abilities it's implied by the frame shape via TYPE_AP (and not even injectively — star
   = attack = 1AP and diamond = quick = 1AP are two shapes for one cost). On spells it's the
   inner CAST_SHAPE ring at R-8, i.e. two concentric polygons 8 units apart — about 3.5 real
   pixels once the action bar scales a 96px sigil into a ~44px slot (action-bar.ts renders
   iconSize:'l' by design, so the L2 detail that differentiates ~10k abilities is preserved).
   So AP gets its own EXPLICIT, type-independent cue in one fixed reading position.

   VOCABULARY: pips with a near-black border — the same dot language the 0ap/1ap/2ap/3ap CDN icons
   already established (SimpleCustomIcon::LIST), so the badge reads as "action points" to anyone who
   has used the site. The border is load-bearing: it's what keeps a pip legible on top of ANY frame.

   ⚠ SPELL / MENTA / CURSE ONLY (owner 2026-07-24; menta + curse added 2026-07-30). The ABILITY
   family reads its cost fine from the frame shape alone — an octagon IS 3AP, with no competing
   meaning — so a badge there is redundant clutter. The three casting types are the ones that
   overload the shape channel: spell = outer sphere + inner cast shape, menta = outer manifestation
   heptagon + inner cast shape, and curse is worse still — its down-triangle states only "curse"
   and draws NO inner ring at all, so before this badge a curse's AP was nowhere on the sigil.

   opt.apBadge picks the placement; '' / 'off' (default) draws nothing, so every production
   surface is unchanged until a variant is chosen. */
const AP_PIP='#ffd60a';        // YELLOW (owner's call) — it has to win against the sphere textures,
                               // which are blue (Arcane) / white (Divine) / green (Primal); the old
                               // blue pip vanished straight into an Arcane frame.
const AP_PIP_EDGE='#05070c';   // near-black border. Chosen over a warm/brown outline because yellow's
                               // whole job here is maximum pop, and near-black is the strongest
                               // available contrast — it's also what every other disc in the sigil
                               // already backs onto, so the badge reads as native rather than bolted on.
const AP_PLATE='#0c0f17';      // same backing as every other satellite disc
const AP_REACTION='#c084fc';   // reaction violet
const AP_PASSIVE='#8b93a7';
/* the shipped default for spells — variant C. Change this ONE constant to re-pick. */
export const AP_BADGE_DEFAULT='rim';
export const AP_BADGE_VARIANTS:Array<{k:string;label:string}>=[
  {k:'off',label:'off (no AP badge)'},
  {k:'br', label:'A · bottom-right plate'},
  {k:'bc', label:'B · bottom-centre plate'},
  {k:'rim',label:'C · rim meter (3 slots) — SHIPPED'},
  {k:'arc',label:'D · rim arc (3 segments)'},
];
type ApCue={n:number;kind:'ap'|'reaction'|'passive'|'sanguine'|'timer';label:string};
/* ONE cost resolver for EVERY item type. compile() already normalises ir.cast for non-spells
   (line ~498 maps TYPE_AP → 'reaction'/'passive'/'free'/'Nap'), so spells, menta, curses and
   the whole ability family all funnel through the same field — no per-type branching here. */
function apCue(ir:RuneIR):ApCue{   // always resolves — every item type has SOME cost to state
  if(ir.itype==='sanguine')return{n:0,kind:'sanguine',label:'Sanguine — costs charges, not AP'};
  const c=ir.cast||'';
  if(TIMER_CASTS.has(c))return{n:0,kind:'timer',label:`Casting time: ${CAST_LABEL[c]||c}`};
  if(c==='reaction')return{n:0,kind:'reaction',label:'Costs a reaction'};
  if(c==='passive')return{n:0,kind:'passive',label:'Passive — costs no action'};
  const n=AP_OF[c];
  if(n!=null)return{n,kind:'ap',label:`${n} AP`};
  return{n:0,kind:'ap',label:'Free (no AP)'};}
/* one pip: filled = this AP is spent by the ability, hollow = an unspent slot (meter variants). */
function apPip(g:SVGElement,x:number,y:number,r:number,filled:boolean,col:string):void{
  g.appendChild(el('circle',{cx:x,cy:y,r,fill:filled?col:'none',
    stroke:filled?AP_PIP_EDGE:col,'stroke-width':filled?1.25:1.3,opacity:filled?1:.55}));}
/* the non-numeric costs share the SAME reading position as the pips — one slot, one glyph,
   so the eye always looks in one place for "what does this cost me". */
function apMark(g:SVGElement,x:number,y:number,kind:ApCue['kind']):void{
  if(kind==='reaction'){apPip(g,x,y,5,true,AP_REACTION);
    g.appendChild(el('path',{d:`M${x+2.2} ${y-1.6} A2.6 2.6 0 1 1 ${x-2.2} ${y-0.8}`,fill:'none',stroke:'#fff','stroke-width':1.2,'stroke-linecap':'round'}));
    g.appendChild(el('path',{d:`M${x+1.3} ${y-3} L${x+2.9} ${y-1.1} L${x+0.8} ${y-0.7} Z`,fill:'#fff'}));return;}
  if(kind==='passive'){apPip(g,x,y,5,false,AP_PASSIVE);return;}
  if(kind==='sanguine'){const r=5.2;   // blood drop — matches the sanguine teardrop frame
    g.appendChild(el('path',{d:`M${x} ${y-r} C${x+r*0.92} ${y-r*0.25} ${x+r} ${y+r*0.45} ${x} ${y+r} C${x-r} ${y+r*0.45} ${x-r*0.92} ${y-r*0.25} ${x} ${y-r} Z`,
      fill:TYPE_COLOR.sanguine||'#e0245e',stroke:AP_PIP_EDGE,'stroke-width':1.1,'stroke-linejoin':'round'}));return;}
  if(kind==='timer'){apPip(g,x,y,5.4,true,'#e2e8f0');
    const t=el('text',{x,y:y+2.6,fill:'#0c0f17','font-size':7.5,'text-anchor':'middle','font-weight':'800'});
    t.textContent='⏲';g.appendChild(t);return;}
  apPip(g,x,y,5,false,AP_PIP);}   // 0 AP = a hollow YELLOW slot: "a cost slot, and it's free"
/* PLATE variants (A/B): N pips in a row on a dark rounded plate. 0AP/reaction/passive/etc.
   collapse to the single kind-glyph, in the same slot. */
function apPlate(g:SVGElement,x:number,y:number,cue:ApCue):void{
  const GAP=10.6, r=4.3, slots=cue.kind==='ap'&&cue.n>0?cue.n:1;
  const w=slots*GAP+5.5, h=14;
  g.appendChild(el('rect',{x:x-w/2,y:y-h/2,width:w,height:h,rx:h/2,
    fill:AP_PLATE,opacity:.92,stroke:'#e2e8f0','stroke-width':.7,'stroke-opacity':.32}));
  if(cue.kind!=='ap'||cue.n<=0){apMark(g,x,y,cue.kind);return;}
  const x0=x-(slots-1)*GAP/2;
  for(let i=0;i<slots;i++)apPip(g,x0+i*GAP,y,r,true,AP_PIP);}
/* METER variants (C/D): ALWAYS three slots — filled = what this costs, hollow = what you'd have
   left. Reads as "N of your 3 AP" rather than a bare count, and it rides the frame rim, which is
   the one band no satellite occupies. */
function apRim(g:SVGElement,cx:number,cy:number,R:number,cue:ApCue,arc:boolean):void{
  /* radii are capped so the badge stays INSIDE the 0..100 viewBox: at 90° the bottom of a pip is
     cy+r_ring+r_pip, so R+5 (=95) + 4.3 just clears 100, and the arc's half-stroke needs R+4. */
  /* kind-glyphs are fatter than a pip (the timer disc is r5.4 + stroke), so they sit one notch
     further in than the pip ring — cy+R+4 put the timer's bottom edge at 100.03, just outside. */
  if(cue.kind!=='ap'||cue.n<=0){apMark(g,cx,cy+R+2,cue.kind);return;}
  /* SPACING (owner 2026-07-24: "spaced out a little more"). At radius 45 the angular step IS the
     gap: 100/90/80 put centres only 7.8 apart, so r4.1 pips actually OVERLAPPED by 0.4 — they read
     as one smear, not three slots. 108/90/72 gives ~14 between centres = a clean ~5.9 of daylight.
     That spread only became affordable once the AP-change disc moved off this band (see below) —
     at its old x=61.5 edge the right-hand pip went straight through it. */
  const angles=[108,90,72];
  if(!arc){angles.forEach((a,i)=>{const[x,y]=P(cx,cy,R+5,a);apPip(g,x,y,4.3,i<cue.n,AP_PIP);});return;}
  const rr=R+4;
  /* black casing FIRST at a wider stroke, colour on top — the arc equivalent of the pip's
     near-black border, so a segment reads against the frame it sits on. */
  angles.forEach((a,i)=>{const[x0,y0]=P(cx,cy,rr,a-6),[x1,y1]=P(cx,cy,rr,a+6);   // ±6 not ±8: at the wider 108/90/72 spread the casings merged
    const d=`M${x0} ${y0} A${rr} ${rr} 0 0 0 ${x1} ${y1}`;
    g.appendChild(el('path',{d,fill:'none',stroke:AP_PIP_EDGE,'stroke-width':i<cue.n?7.6:5,'stroke-linecap':'round',opacity:.9}));
    g.appendChild(el('path',{d,fill:'none',stroke:i<cue.n?AP_PIP:AP_PASSIVE,
      'stroke-width':i<cue.n?5.2:2.6,'stroke-linecap':'round',opacity:i<cue.n?1:.5}));});}
function renderApBadge(s:SVGElement,ir:RuneIR,cx:number,cy:number,R:number,variant:string):void{
  const cue=apCue(ir);   // always resolves — no null guard needed
  const g=part(s,'Action Cost',cue.label);
  /* 'br' sits at cy+26, NOT the cy+31 it started at: the plate is 14 tall, and the AP-change disc's
     top edge is y=83.5, so any centre below cy+26 overlaps it. Cost and cost-MODIFIER overlapping is
     the one collision worth engineering away — they're the two things most easily confused. */
  if(variant==='br')      apPlate(g,cx+27,cy+26,cue);   // owner's proposal: bottom-right corner
  else if(variant==='bc') apPlate(g,cx,cy+40,cue);      // bottom-centre (recharge + AP-change shift aside)
  else if(variant==='rim')apRim(g,cx,cy,R,cue,false);
  else if(variant==='arc')apRim(g,cx,cy,R,cue,true);}

function renderSigil(ir:RuneIR,size:number,opt:RuneOpts):SVGElement{
  const s=svgEl(size),cx=50,cy=50, R=40;
  const d0=dmg(ir.damage[0]||'Force');
  const showSat=!!opt.sats, useCdn=!!opt.cdn;
  /* LEVEL OF DETAIL: 2=full(≥96, everything), 1=standard(72–88, curated but no generic tokens),
     0=compact(≤64, identity only). opt.lod forces a tier (preview control); else derive from size.
     Elements are pruned by data-tag via LABEL_LOD after the SVG is built. */
  const lod=(opt.lod!=null&&opt.lod!=='')?(+opt.lod):(size>=96?2:size>=72?1:0);
  const durN=DUR_RINGS[ir.durType]??0, ap=AP_OF[ir.cast]||0;
  /* AP-badge variant, resolved ONCE: several blocks below step aside for it (recharge disc,
     AP-change disc, the standalone casting-timer emblem).
     SPELL / MENTA / CURSE ONLY — an ability's frame shape already states its cost unambiguously,
     so gating here means no ability render changes at all, whatever the lab's picker is set to.
     DEFAULT = 'rim' (variant C, owner-selected 2026-07-24): three yellow slots on the bottom rim,
     filled = this item's AP, hollow = what you'd have left of your 3. Unset/'' takes the default;
     only an explicit 'off' suppresses it, which is what the admin lab's picker sends to A/B it.
     ⚠ apCue() is already type-agnostic and compile() normalises menta/curse cast off
     spell_casting_time (line ~517), so extending the gate is the WHOLE change — the pips, the
     satellite relocations below and the hover label all follow for free. */
  const apBadged=ir.isSpell||ir.itype==='menta'||ir.itype==='curse';
  const apVar=apBadged?(v=>v==='off'?'':(v||AP_BADGE_DEFAULT))(String(opt.apBadge||'')):'';
  const cc=ir.isSpell?(CIRCLE_COLOR[ir.circle??'']||'#9ca3af'):(TYPE_COLOR[ir.itype]||'#9aa7c7');
  const defs=el('defs');s.appendChild(defs);

  /* TEAM = directional arrowheads around the rim: enemies point OUTWARD (red),
     allies point INWARD (blue). */
  if(ir.sameTeam||ir.oppTeam){const outward=ir.oppTeam, tc=outward?'#ef5a5a':'#3b82f6';
    const tg=part(s,'Team',outward?'Targets: enemies only':'Targets: allies only');
    teamArrows(tg,cx,cy,outward,tc);}

  if(ir.isSpell){
    const sfill=texGradient(defs,sphereTex(ir.sphere));
    const sshape=sphereShape(ir.sphere);
    const gSph=part(s,'Sphere',`Sphere: ${ir.sphereAll.join(' + ')||'—'}`);
    drawFrame(gSph,cx,cy,R,sshape,sfill,3);
    /* INNER ring = casting-time AP as a SHAPE (unifies with the ability type-shapes:
       1→diamond, 2→hexagon, 3→octagon; circle = special/timer cast). Tinted by Circle of Magic
       COLOUR (cc) — circle-of-magic is colour-only now, no longer its own shape. */
    const cshape=CAST_SHAPE[ir.cast]||'circle';
    const gCirc=part(s,'Circle of Magic',`Circle of Magic: ${ir.circle||'—'} · ${CAST_LABEL[ir.cast]||ir.cast}`);
    drawFrame(gCirc,cx,cy,R-8,cshape,cc,2.4);
  } else {
    if(ir.itype==='menta'&&ir.manifestation){
      /* MENTA (parallel to spells): outer heptagon COLOURED by manifestation; inner shape = AP. */
      const mcol=manifestColor(ir.manifestation);
      const gT=part(s,'Manifestation',`Manifestation: ${ir.manifestation}`);
      drawFrame(gT,cx,cy,R,'heptagon',mcol,2.8);
      const mshape=CAST_SHAPE[ir.cast]||'circle';
      const gAp=part(s,'Action Cost',`${ir.manifestation} · ${CAST_LABEL[ir.cast]||ir.cast}`);
      drawFrame(gAp,cx,cy,R-8,mshape,mcol,2.4);
    } else if(ir.itype==='sanguine'){
      /* SANGUINE (sorcerer) — a BLOOD-TEARDROP frame (pointed top, bulbous bottom), blood-red.
         Essentially a free ability (costs charges, not AP). */
      const gT=part(s,'Ability Type','Ability type: Sanguine');
      gT.appendChild(el('path',{d:`M${cx} ${cy-R} C${cx+R*0.92} ${cy-R*0.25} ${cx+R} ${cy+R*0.45} ${cx} ${cy+R} C${cx-R} ${cy+R*0.45} ${cx-R*0.92} ${cy-R*0.25} ${cx} ${cy-R} Z`,
        fill:'none',stroke:TYPE_COLOR.sanguine||'#e0245e','stroke-width':2.8,'stroke-linejoin':'round'}));
    } else {
      const tshape=TYPE_SHAPE[ir.itype]||'circle';
      const gT=part(s,'Ability Type',`Ability type: ${TYPE_LABEL[ir.itype]||ir.itype}`);
      drawFrame(gT,cx,cy,R,tshape,cc,2.8);
    }
  }

  if(durN>0){const gDur=part(s,'Duration',
      `Duration: ${durLabel(ir)}${ir.persistentAoe?' · persistent AoE (follows)':''}`);
    /* bigger + bolder now that the inner circles are gone: top ring r31, more spacing, thicker
       stroke + higher opacity so multi-round durations actually read. */
    const r0=31;
    for(let i=0;i<durN;i++)gDur.appendChild(el('circle',{cx,cy,r:r0-i*3.8,fill:'none',
      stroke:ir.persistentAoe?'#fbbf24':cc,'stroke-width':ir.persistentAoe?1.3:1.1,
      opacity:ir.persistentAoe?.7:(.62-i*0.07),'stroke-dasharray':ir.persistentAoe?'2 3':'none'}));
    if(ir.persistentAoe){const[dx,dy]=P(cx,cy,r0,-90);
      gDur.appendChild(el('circle',{cx:dx,cy:dy,r:2,fill:'#fbbf24'}));}}

  /* SHORT (turn-based) DURATION — a DIAMOND in the inner ring instead of round duration rings,
     + a small dot: CASTER short-duration = dot at TOP, TARGET short-duration = dot at BOTTOM. */
  if(ir.shortCaster||ir.shortTarget){
    const gSd=part(s,'Short Duration',
      `Ends ${[ir.shortCaster?'on caster turn':null,ir.shortTarget?'on target turn':null].filter(Boolean).join(' / ')}`);
    const dr=24, dpts=[[cx,cy-dr],[cx+dr,cy],[cx,cy+dr],[cx-dr,cy]].map(p=>p.join(',')).join(' ');
    gSd.appendChild(el('polygon',{points:dpts,fill:'none',stroke:cc,'stroke-width':.8,opacity:.5,'stroke-dasharray':'2 2'}));
    /* CASTER next-turn (start/end) = dot WITH a ring (top); TARGET turn (start/end) = BARE dot (bottom).
       Ring-vs-no-ring tells you whose turn it ends on. */
    if(ir.shortCaster){gSd.appendChild(el('circle',{cx,cy:cy-dr,r:2,fill:cc}));
      gSd.appendChild(el('circle',{cx,cy:cy-dr,r:4,fill:'none',stroke:cc,'stroke-width':1}));}
    if(ir.shortTarget)gSd.appendChild(el('circle',{cx,cy:cy+dr,r:2,fill:cc}));}

  /* AURA — self-centered AoE that FOLLOWS the caster (persistent_aoe + aoe_follows_target + range Self).
     Draw EMANATING dashed rings around the whole frame, team-tinted (red=enemies/blue=allies/else
     circle-of-magic) so it reads as an aura rather than a placed zone. */
  if(ir.isAura){const ac=ir.oppTeam?'#ef5a5a':ir.sameTeam?'#3b82f6':cc;
    const gAu=part(s,'Aura',`Aura: ${ir.aoe||'sphere'}${ir.aoeDist?' '+ir.aoeDist+'ft':''} · follows caster`);
    for(let i=0;i<3;i++)gAu.appendChild(el('circle',{cx,cy,r:R+2+i*3,fill:'none',stroke:ac,'stroke-width':1.2,
      opacity:.5-i*0.13,'stroke-dasharray':'1 4'}));}

  if(ir.aoe){const acol=ir.isAura?(ir.oppTeam?'#ef5a5a':ir.sameTeam?'#3b82f6':cc):cc;
    const gAoe=part(s,'Area',`${ir.isAura?'Aura':'Area'}: ${ir.aoe}${ir.aoeDist?' '+ir.aoeDist+'ft':''}`);
    const bx=cx+27, by=cy+30;
    /* PRIORITY: punch a dark backing under the badge so the frame + team ring don't bleed through. */
    gAoe.appendChild(el('circle',{cx:bx,cy:by,r:11,fill:'#0c0f17'}));
    aoeBadge(gAoe,bx,by,ir.aoe,acol);
    /* AoE SIZE = a VERTICAL stack of dots just right of the badge. ONE DOT PER 10ft, capped at 6.
       Standard+ only (lod≥1) — too small to read at Compact/64 (badge shape stays, dots drop). */
    if(lod>=1&&ir.aoeDist){const n=Math.max(1,Math.min(6,Math.ceil(ir.aoeDist/10)));
      const dx=bx+13, y0=by-(n-1)*1.9;
      for(let i=0;i<n;i++){
        gAoe.appendChild(el('circle',{cx:dx,cy:y0+i*3.8,r:1.5,fill:cc,opacity:.55+i*0.45/n}));}}}

  /* SPEED CHANGE (remaining) — boot/wing/fin + ↑faster(green)/↓slower(red); value omitted (icon tells it).
     Bottom-left. Multiple types → one generic boot + dominant direction; hover lists them all. */
  if(ir.speedChanges&&ir.speedChanges.length){const sc=ir.speedChanges;
    const allUp=sc.every((z)=>z.up), allDn=sc.every((z)=>!z.up);
    const col=allUp?'#34d399':allDn?'#ef5a5a':'#fbbf24';
    const gSp=part(s,'Speed Change','Speed: '+sc.map((z)=>`${z.kind} ${z.val}`).join(', '));
    const first=sc[0]!;
    const x=cx-22,y=cy+34, k=sc.length>1?'boot':first.kind;
    gSp.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:col,'stroke-width':1.4}));
    speedMotif(gSp,x,y,sc.length>1?'walking':first.kind,col);
    const dc=allDn?1:-1; // chevron points up (faster) unless purely slower
    gSp.appendChild(el('path',{d:`M${x+3.5} ${y-3.5+dc*1.6} L${x+6} ${y-3.5+dc*-1} L${x+8.5} ${y-3.5+dc*1.6}`,fill:'none',stroke:col,'stroke-width':1.3,'stroke-linecap':'round','stroke-linejoin':'round'}));}

  /* TELEPORT (change_teleport_remaining) — lives in the SPEED area but distinct: purple portal glyph.
     Same zone as speed (it IS movement), different colour + icon per user. */
  if(ir.teleportChange){const x=cx-22,y=cy+21,tcol='#a855f7';
    const gTp=part(s,'Teleport',`Teleport (remaining): ${ir.teleportChange}`);
    gTp.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:tcol,'stroke-width':1.4}));
    gTp.appendChild(el('circle',{cx:x,cy:y,r:3.6,fill:'none',stroke:tcol,'stroke-width':1.1,'stroke-dasharray':'1.6 1.6'})); // portal
    gTp.appendChild(el('path',{d:`M${x-3.4} ${y} H${x+1.2} M${x-0.6} ${y-2.2} L${x+1.6} ${y} L${x-0.6} ${y+2.2}`,fill:'none',stroke:tcol,'stroke-width':1.1,'stroke-linecap':'round','stroke-linejoin':'round'}));}

  /* SPEED GRANTS (base speed_flying/climbing/etc) — GRANTS a movement MODE (e.g. flight 60), distinct
     from the remaining-movement change above. Each mode = its own small green disc (wing/fin/boot),
     left flank stacked upward from center. Marquee info ("you can fly now"), so each mode is shown. */
  if(ir.speedGrants&&ir.speedGrants.length){const sg=ir.speedGrants, gcol='#34d399';
    const shown=sg.slice(0,4), extra=sg.length-shown.length;
    shown.forEach((z,i)=>{const[x,y]=P(cx,cy,R-5,150+i*15); // fan along the lower-left arc
      const gG=part(s,'Speed Grant',`Grants ${z.kind} speed ${z.val}`);
      gG.appendChild(el('circle',{cx:x,cy:y,r:6.5,fill:'#0c0f17',stroke:gcol,'stroke-width':1.4}));
      speedMotif(gG,x,y,z.kind,gcol);});
    if(extra>0){const[x,y]=P(cx,cy,R-5,150+shown.length*15);
      const gG=part(s,'Speed Grant',`+${extra} more speed grant${extra>1?'s':''}`);
      gG.appendChild(el('circle',{cx:x,cy:y,r:6.5,fill:'#0c0f17',stroke:gcol,'stroke-width':1.4}));
      const t=el('text',{x,y:y+2.5,fill:gcol,'font-size':7,'text-anchor':'middle','font-weight':'800'});
      t.textContent='+'+extra;gG.appendChild(t);}}

  /* SPELL LEVEL is drawn LATE (just before the generic tier) so it sits ABOVE the damage spray. */

  /* tick colour = damage colour if there IS a damage type, else the roll-family colour (heal=green,
     mana=blue, …) so non-damage dice (temp-vitality/heal/mana) don't read as grey "Force". */
  const tickCol=ir.damage.length?d0.c:rollFamilyColor(rollGlyph(ir.roll));
  /* DAMAGE TICKS — count = #dice (cap 12), length ∝ die size. More pronounced now (thicker + longer),
     and they start OUTSIDE the core (r 17, core is r12) so they never pierce into the damage disc. */
  if(ir.dice&&ir.die){const gAmt=part(s,'Dice',`${ir.dice}d${ir.die}${ir.mod?(ir.mod>0?'+':'')+ir.mod:''} (total ${ir.avg})${ir.roll?' · '+ir.roll:''}`);
    const n=Math.min(ir.dice,12), len=3.4+(ir.die/12)*5;
    for(let i=0;i<n;i++){const a=-90+i*360/n;const[x1,y1]=P(cx,cy,17,a),[x2,y2]=P(cx,cy,17+len,a);
      gAmt.appendChild(el('line',{x1,y1,x2,y2,stroke:tickCol,'stroke-width':2.3,'stroke-linecap':'round',opacity:.95}));}}
  else if(ir.dynMod){
    /* DYNAMIC amount (mod = [total_level]/[body]/… — can't compute a total) → a ring of short WAVY
       ticks where the dice ticks would go, signalling "a variable amount", no number. */
    const gAmt=part(s,'Dice',`Dynamic amount: ${ir.modRaw}${ir.roll?' · '+ir.roll:''}`);
    const n=8;
    for(let i=0;i<n;i++){const a=-90+i*360/n;
      const[x1,y1]=P(cx,cy,17,a),[xm,ym]=P(cx,cy,19.4,a+3),[x2,y2]=P(cx,cy,22,a);
      gAmt.appendChild(el('path',{d:`M${x1} ${y1} Q${xm} ${ym} ${x2} ${y2}`,fill:'none',stroke:tickCol,'stroke-width':1.8,'stroke-linecap':'round',opacity:.9}));}}

  const dcols=ir.damage.map((d:string)=>dmg(d).c);
  const diceStr=(ir.dice&&ir.die)?`${ir.dice}d${ir.die}${ir.mod?(ir.mod>0?'+':'')+ir.mod:''}`:(ir.mod?`${ir.mod>0?'+':''}${ir.mod}`:'—');
  const dlabel=ir.damage.length?`Damage: ${ir.damage.join(' + ')} · ${diceStr}${ir.avg!=null?` (total ${ir.avg})`:''}`
    :(ir.summon.length?`Summons: ${ir.summon.join(', ')}`
     :(ir.fabricate.length?`Fabricates: ${ir.fabricate.join(', ')}`
     :`Effect: ${ir.circle||TYPE_LABEL[ir.itype]||'utility'}`));
  const gCore=part(s,'Core',dlabel);
  if(ir.damage.length){
    pieDisc(gCore,cx,cy,12,dcols);
    gCore.appendChild(el('circle',{cx,cy,r:12,fill:'none',stroke:'#0c0f17','stroke-width':1.5}));
    /* SINGLE damage type → draw the type's distinct glyph over the colour (shape = type; colour-blind
       backstop + disambiguates lookalike colours). MULTI-type stays the clean colour pie (no glyphs). */
    if(ir.damage.length===1){const damage=ir.damage[0],damageColor=dcols[0];if(damage&&damageColor)dmgGlyph(gCore,cx,cy,7,damage,glyphInk(damageColor));}
  } else if(ir.coreImg){
    /* SUMMON / FABRICATE — show the actual item's picture in the core, clipped to the disc */
    const cid='clip'+(_gid++);
    const cp=el('clipPath',{id:cid});cp.appendChild(el('circle',{cx,cy,r:12}));defs.appendChild(cp);
    gCore.appendChild(el('circle',{cx,cy,r:12,fill:'#0c0f17'}));
    const img=el('image',{x:cx-12,y:cy-12,width:24,height:24,href:ir.coreImg,
      'preserveAspectRatio':'xMidYMid slice','clip-path':`url(#${cid})`});
    img.setAttribute('onerror','this.remove()');gCore.appendChild(img);
    gCore.appendChild(el('circle',{cx,cy,r:12,fill:'none',stroke:cc,'stroke-width':1.4}));
  } else if(ir.summon.length||ir.fabricate.length){
    /* SUMMON / FABRICATE with NO picture attached (lean data, no full-nest) → draw the SUMMON GLYPH
       in the core so "this conjures something" still reads centrally. The coreImg branch above wins
       whenever the exporter attached a thumbnail. Violet = conjuration. */
    const scol='#a78bfa';
    gCore.appendChild(el('circle',{cx,cy,r:12,fill:'#0c0f17',stroke:scol,'stroke-width':1.5}));
    rollTypeGlyph(gCore,cx,cy,8,scol,'summon');
  } else if(ir.shapeshift.length){
    /* SHAPESHIFT (user_choice_*_shapeshift) — central morph glyph (teal) so "you transform" reads at
       center, same slot as summon. No creature pic (lean data); hover lists the forms. */
    const sc='#22d3ee';
    gCore.appendChild(el('circle',{cx,cy,r:12,fill:'#0c0f17',stroke:sc,'stroke-width':1.5}));
    rollTypeGlyph(gCore,cx,cy,8,sc,'shapeshift');
  }
  /* NO ELSE — the CORE is reserved for damage (pie disc) and summon/fabricate art only. When an ability
     has neither, the centre is left EMPTY: a ring that depicts nothing has no purpose, so it's removed.
     Non-damage info lives in its own zones (roll emblem up top, status satellites, etc.). */

  if(ir.concentration){const g=part(s,'Concentration','Concentration: maintained while focused');
    g.appendChild(el('circle',{cx,cy:cy-13,r:6.5,fill:'#0c0f17'}));
    star(g,cx,cy-13,5.8,'#fff');}

  /* TOP roll/summon emblem — kept big (user favourite). Summon now lives in the CORE (thumbnail or
     glyph), so only echo it as the top emblem when the core is busy with DAMAGE art. */
  const summonInCore = (ir.summon.length||ir.fabricate.length) && !ir.damage.length;
  const rk = ir.roll ? rollGlyph(ir.roll) : ((ir.summon.length && !summonInCore) ? 'summon' : null);
  const ry=cy-R+5;
  if(rk){const gR=part(s,'Roll Type', ir.roll?`Roll: ${ir.roll}${ir.avg!=null?` · total ${ir.avg}`:''}`:`Summon Roll: ${ir.summon.join(', ')}`);
    rollTypeGlyph(gR,cx,ry,9,cc,rk);}
  /* CONTEST → HALF rune is drawn LATE (near the end) so it sits ON TOP of the duration rings, clean. */

  /* ROLL TYPE ADV/DIS (+ AGAINST) — the roll-type glyph in a ring, blue=ADV / red=DIS; "against" gets a
     pip. A horizontal row across the TOP (above the roll emblem, between the contest discs). Slightly
     larger than a generic token since it matters. */
  if(ir.rollAdvDis&&ir.rollAdvDis.length){
    const list=ir.rollAdvDis, shown=list.slice(0,4), extra=list.length-shown.length, n=shown.length;
    const ry0=5, step=11, x0=cx-(n-1)*step/2;
    shown.forEach((rd,i)=>{const col=rd.adv?'#3b82f6':'#ef5a5a', rx2=x0+i*step;
      const gRd=part(s,'Roll Adv/Dis',`${rd.adv?'Advantage':'Disadvantage'} on ${rd.against?'rolls made against · ':''}${rd.rt}`);
      gRd.appendChild(el('circle',{cx:rx2,cy:ry0,r:4.6,fill:'#0c0f17',stroke:col,'stroke-width':1.5}));
      rollTypeGlyph(gRd,rx2,ry0,3,col,rollGlyph(rd.rt));});  // "against" signalled globally by the top → arrow
    if(extra>0){const rx2=x0+n*step;
      part(s,'Roll Adv/Dis',`+${extra} more roll adv/dis`).appendChild(el('path',{d:`M${rx2-3.4} ${ry0} A3.4 3.4 0 0 0 ${rx2+3.4} ${ry0}`,fill:'none',stroke:'#94a3b8','stroke-width':1}));}}

  /* CONTEST vs SPELLS — a serious defensive trait (≈ magic resistance), so a PROMINENT spell-ward
     SHIELD (blue=ADV / red=DIS) with a spell-spark, not a little generic token. Upper-left, outside frame. */
  if(ir.spellContest){const adv=ir.spellContest==='adv', col=adv?'#3b82f6':'#ef5a5a';
    const[wx,wy]=P(cx,cy,R+6,-152), rr=6.4;
    const gW=part(s,'Spell Contest',`${adv?'Advantage':'Disadvantage'} on contesting rolls vs spells`);
    gW.appendChild(el('path',{d:`M${wx} ${wy-rr} L${wx+rr} ${wy-rr*0.5} V${wy+rr*0.15} Q${wx+rr} ${wy+rr} ${wx} ${wy+rr*1.2} Q${wx-rr} ${wy+rr} ${wx-rr} ${wy+rr*0.15} V${wy-rr*0.5} Z`,
      fill:'#0c0f17',stroke:col,'stroke-width':1.7,'stroke-linejoin':'round'}));
    gW.appendChild(el('path',{d:`M${wx} ${wy-3} L${wx+1} ${wy-0.6} L${wx+3} ${wy} L${wx+1} ${wy+0.6} L${wx} ${wy+3.4} L${wx-1} ${wy+0.6} L${wx-3} ${wy} L${wx-1} ${wy-0.6} Z`,fill:col})); // spell spark
    const d=adv?1:-1; // small chevron: up=adv / down=dis
    gW.appendChild(el('path',{d:`M${wx-2.4} ${wy+rr*0.78+d*1.2} L${wx} ${wy+rr*0.78-d*1.2} L${wx+2.4} ${wy+rr*0.78+d*1.2}`,fill:'none',stroke:col,'stroke-width':1.3,'stroke-linecap':'round','stroke-linejoin':'round'}));}

  /* CONTEST vs STATUS EFFECTS — the status's own icon in a THIN blue(adv)/red(dis) ring, PLUS a small
     opposed-arrows (contest) mark beneath it so it's clearly "a contest about this status", distinct
     from the regular status satellites. Left-edge vertical stack, outside the frame. */
  if(ir.contestVsStatus&&ir.contestVsStatus.length){
    const list=ir.contestVsStatus, shown=list.slice(0,3), extra=list.length-shown.length, vx=cx-R-3;
    shown.forEach((cvs,i)=>{const col=cvs.adv?'#3b82f6':'#ef5a5a', vy=cy-13+i*12;
      const sm=STATUS_META[(cvs.name||'').toLowerCase()], icon=(sm&&sm.v)?sm.v:cvs.name;
      const gV=part(s,'Contest vs Status',`${cvs.adv?'Advantage':'Disadvantage'} on contesting rolls vs ${cvs.name}`);
      gV.appendChild(el('circle',{cx:vx,cy:vy,r:5,fill:'#0c0f17',stroke:col,'stroke-width':1}));
      artNode(gV,icon,'ring',vx,vy,3.6,col,useCdn,true);
      gV.appendChild(el('path',{d:`M${vx-3.4} ${vy+5.6} L${vx-1.2} ${vy+5.6} M${vx-2.3} ${vy+4.8} L${vx-1.2} ${vy+5.6} L${vx-2.3} ${vy+6.4} M${vx+3.4} ${vy+5.6} L${vx+1.2} ${vy+5.6} M${vx+2.3} ${vy+4.8} L${vx+1.2} ${vy+5.6} L${vx+2.3} ${vy+6.4}`,fill:'none',stroke:col,'stroke-width':0.8,'stroke-linecap':'round','stroke-linejoin':'round'}));});
    if(extra>0){const vy=cy-13+3*12;
      part(s,'Contest vs Status',`+${extra} more contest-vs-status`).appendChild(el('path',{d:`M${vx-3.5} ${vy} A3.5 3.5 0 0 0 ${vx+3.5} ${vy}`,fill:'none',stroke:'#94a3b8','stroke-width':1}));}}

  /* IMMUNE TO STATUS EFFECTS — each status's icon in a SHINY CHROME ring (metallic = "can't touch me").
     Right-edge vertical stack, outside the frame, distinct from the blue/red contest-vs-status on the left. */
  if(ir.immuneStatus&&ir.immuneStatus.length){
    const chrome=texGradient(defs,['#ffffff','#cbd5e1','#8a97a8','#eef2f7','#64748b','#ffffff']);
    const list=ir.immuneStatus, shown=list.slice(0,3), extra=list.length-shown.length, ix=cx+R+2;
    shown.forEach((nm:string,i:number)=>{const iy=cy-13+i*12;
      const sm=STATUS_META[(nm||'').toLowerCase()], icon=(sm&&sm.v)?sm.v:nm;
      const gI=part(s,'Immune',`Immune to status effect: ${nm}`);
      gI.appendChild(el('circle',{cx:ix,cy:iy,r:5.4,fill:'#0c0f17',stroke:chrome,'stroke-width':1.4}));
      artNode(gI,icon,'ring',ix,iy,4.2,'#cbd5e1',useCdn,true);});  // thinner chrome (was 2.2, hid the icon) + bigger icon
    if(extra>0){const iy=cy-13+3*12;
      part(s,'Immune',`+${extra} more status immunities`).appendChild(el('path',{d:`M${ix-3.5} ${iy} A3.5 3.5 0 0 0 ${ix+3.5} ${iy}`,fill:'none',stroke:'#cbd5e1','stroke-width':1}));}}

  /* CONTEST — flank the roll emblem: contester (CASTER, blue) left, contestee (TARGET, red) right.
     Bigger discs, NO fill background — just a colored ring so the skill icon reads clearly. */
  const CASTER_C='#3b82f6', TARGET_C='#ef5a5a';
  if(ir.contesterSkills.length){const sk=ir.contesterSkills[0], extra=ir.contesterSkills.length-1;
    const gC=part(s,'Caster Skill',`Caster contests with: ${ir.contesterSkills.join(', ')}`);
    /* echoed shadow discs OUT TO THE LEFT = more skill options exist */
    for(let e=extra;e>=1;e--)gC.appendChild(el('circle',{cx:cx-21-e*3,cy:ry,r:9,fill:'none',stroke:CASTER_C,'stroke-width':1,opacity:.35}));
    gC.appendChild(el('circle',{cx:cx-21,cy:ry,r:9,fill:'#0c0f17',stroke:CASTER_C,'stroke-width':1.25}));
    artNode(gC,sk,'ring',cx-21,ry,6.4,CASTER_C,useCdn);}
  if(ir.contesteeSkills.length){const sk=ir.contesteeSkills[0], extra=ir.contesteeSkills.length-1;
    const gC=part(s,'Target Skill',`Target defends with: ${ir.contesteeSkills.join(', ')}`);
    for(let e=extra;e>=1;e--)gC.appendChild(el('circle',{cx:cx+21+e*3,cy:ry,r:9,fill:'none',stroke:TARGET_C,'stroke-width':1,opacity:.35}));
    gC.appendChild(el('circle',{cx:cx+21,cy:ry,r:9,fill:'#0c0f17',stroke:TARGET_C,'stroke-width':1.25}));
    artNode(gC,sk,'ring',cx+21,ry,6.4,TARGET_C,useCdn);
    /* TARGET RETRIES — loop badge on the target (right) disc = re-roll each end of turn */
    if(ir.targetRetries){const gR2=part(s,'Retry','Target retries the contest at end of each turn');
      const lx=cx+29,ly=ry+8;
      gR2.appendChild(el('circle',{cx:lx,cy:ly,r:4.6,fill:'#0c0f17',stroke:TARGET_C,'stroke-width':1.2}));
      gR2.appendChild(el('path',{d:`M${lx+2.5} ${ly-1.8} A2.7 2.7 0 1 1 ${lx-2.5} ${ly-0.8}`,fill:'none',stroke:TARGET_C,'stroke-width':1.2,'stroke-linecap':'round'}));
      gR2.appendChild(el('path',{d:`M${lx+1.6} ${ly-3.4} L${lx+3.2} ${ly-1.4} L${lx+1} ${ly-1}`,fill:TARGET_C,stroke:'none'}));}
  }

  /* FIXED DC — "DC against": the value the TARGET rolls against → RIGHT side, target/red.
     (number is the deliberate exception to the no-numbers rule) */
  if(ir.fixedDc!=null){const gD=part(s,'Fixed DC',`Fixed DC (target rolls against): ${ir.fixedDc}`);
    const dx=cx+21, dy=ry+14;
    gD.appendChild(el('circle',{cx:dx,cy:dy,r:6.5,fill:'#0c0f17',stroke:TARGET_C,'stroke-width':1.6}));
    const t=el('text',{x:dx,y:dy+2.5,fill:'#fff','font-size':7.5,'text-anchor':'middle','font-weight':'800'});
    t.textContent=String(ir.fixedDc);gD.appendChild(t);}

  /* TRIGGER = lightning bolt in its OWN dark disc, low-center — contained so it never
     intersects rings/ticks. Important (reactive ability), so kept prominent + central. */
  if(ir.hasTrigger){const gT=part(s,'Trigger',`Triggers on: ${ir.trigLabel}`);
    gT.appendChild(el('circle',{cx,cy:cy+20,r:7.5,fill:'#0c0f17',stroke:'#fbbf24','stroke-width':1.3}));
    gT.appendChild(motifNode('bolt',cx,cy+20,5,'#fbbf24',true));}

  /* LIGHT — creates illumination: a small candle-flame in a disc, upper-LEFT (-120°).
     amber = bright, cool blue = dim-only; a slash = destroys light (no exact ft needed). */
  if(ir.light||ir.destroysLight){const[x,y]=P(cx,cy,R-5,-120);
    const lcol=ir.destroysLight?'#8b93a7':(ir.light==='dim'?'#7aa2ff':'#fbbf24');
    const gL=part(s,'Light',ir.destroysLight?'Destroys light':(ir.light==='dim'?'Creates dim light':'Creates bright light'));
    gL.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:lcol,'stroke-width':1.4}));
    if(ir.destroysLight){ // extinguished: flame outline + slash
      gL.appendChild(motifNode('flame',x,y-0.5,4,lcol,false));
      gL.appendChild(el('line',{x1:x-4.5,y1:y+4.5,x2:x+4.5,y2:y-4.5,stroke:lcol,'stroke-width':1.4,'stroke-linecap':'round'}));
    } else { // lit flame + radiating ticks
      gL.appendChild(motifNode('flame',x,y-0.5,4,lcol,true));
      [-45,0,45].forEach(a=>{const[x1,y1]=P(x,y-0.5,5.5,a-90),[x2,y2]=P(x,y-0.5,7.5,a-90);
        gL.appendChild(el('line',{x1,y1,x2,y2,stroke:lcol,'stroke-width':1,'stroke-linecap':'round',opacity:.7}));});}}

  /* FALL PROTECTION — negate_falling_damage_in_feet → soft-landing mark (down-chevron cushioned on a
     ground arc) lower-right at 68° (clear of the AoE badge ~48° + bottom cluster ~90°). Symbol only,
     no number (per design); amount lives in hover. */
  if(ir.fallNegate){const[x,y]=P(cx,cy,R-5,68);const fc='#a5f3fc';
    const gF=part(s,'Fall',`Negates falling damage (${ir.fallNegate}ft)`);
    gF.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:fc,'stroke-width':1.3}));
    // down-chevron (the fall) landing onto a cushion arc (softened)
    gF.appendChild(el('path',{d:`M${x-3} ${y-3.5} L${x} ${y-0.5} L${x+3} ${y-3.5}`,fill:'none',stroke:fc,'stroke-width':1.4,'stroke-linecap':'round','stroke-linejoin':'round'}));
    gF.appendChild(el('path',{d:`M${x-4} ${y+3} Q${x} ${y+0.5} ${x+4} ${y+3}`,fill:'none',stroke:fc,'stroke-width':1.4,'stroke-linecap':'round'}));}

  /* LIMITED VISIBILITY — limited_visibility_ft (super rare) → fog/mist mark (stacked wavy lines),
     UPPER-LEFT at -142° (threads the gap between the light emblem -120° and skill-adv -160°; this
     arc is the most open and the layer is super rare so overlap risk is minimal). Symbol only;
     the ft value is in hover. */
  if(ir.limitedVis){const[x,y]=P(cx,cy,R-5,-142);const vc='#94a3b8';
    const gV=part(s,'Visibility',`Limited visibility (${ir.limitedVis}ft)`);
    gV.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:vc,'stroke-width':1.3}));
    [-2.5,0.5,3.5].forEach((dy,i)=>{const w=i===1?4:3.2;
      gV.appendChild(el('path',{d:`M${x-w} ${y+dy} Q${x-w/2} ${y+dy-1.4} ${x} ${y+dy} T${x+w} ${y+dy}`,
        fill:'none',stroke:vc,'stroke-width':1.2,'stroke-linecap':'round',opacity:.85-i*0.12}));});}

  /* CHARGE — caster moves toward target: forward double-chevron, bottom-LEFT corner */
  if(ir.moveToward){const gM=part(s,'Charge',`Caster moves ${ir.moveToward}ft toward target`);
    const[x,y]=P(cx,cy,R-6,135);
    gM.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:cc,'stroke-width':1.2}));
    // two chevrons pointing right-ish (toward center) = closing distance
    gM.appendChild(el('path',{d:`M${x-3} ${y-3} L${x} ${y} L${x-3} ${y+3} M${x+0.5} ${y-3} L${x+3.5} ${y} L${x+0.5} ${y+3}`,
      fill:'none',stroke:cc,'stroke-width':1.4,'stroke-linecap':'round','stroke-linejoin':'round'}));}

  /* RANGE — dash ladder in the TOP-RIGHT corner (moved off the right flank so the
     caster status stack can live there). Rungs = reach tier, lengthening outward. */
  const rangeImplied = !ir.isSpell && ['attack_ability','opportunity_attack_ability','passive_ability'].includes(ir.itype);
  if(ir.range && !rangeImplied){
    /* tolerate a resolved-name distance suffix ("Near (60)" — NamedIcon appends x_distance) so the
       tier still classifies; the tooltip keeps the full name incl. distance. */
    const rkey=String(ir.range).replace(/\s*\(\d+\)\s*$/,'').trim();
    const tier=RANGE_TIER[rkey]??0;
    const gRange=part(s,'Range',`Range: ${ir.range}`);
    const bx=cx+34, by=cy-34;
    if(rkey==='Self'){ // SELF = a dot ringed by a circle (distinct from Reach's single rung)
      gRange.appendChild(el('circle',{cx:bx+3,cy:by,r:3.2,fill:'none',stroke:cc,'stroke-width':1.4}));
      gRange.appendChild(el('circle',{cx:bx+3,cy:by,r:1,fill:cc}));}
    else{const cnt=Math.max(tier,1);
      for(let i=0;i<cnt;i++){const y=by+i*6;
        const ln=4+ (tier===0?0:i*1.2);
        gRange.appendChild(el('line',{x1:bx,y1:y,x2:bx+ln+3,y2:y,stroke:cc,
          'stroke-width':2,'stroke-linecap':'round',opacity:.55+i*0.45/cnt}));}}}

  /* CONDITION gate — "fires only if a status is (or isn't) present". Shows the real status ICON
     on a PURPLE-ringed disc, right edge; dashed ring = "lacks" (not_active). Hover gives detail. */
  if(ir.conditionStatuses&&ir.conditionStatuses.length){const c0=ir.conditionStatuses[0]!;
    const[x,y]=P(cx,cy,R-4,25);
    const gIf=part(s,'Condition',`Condition: ${ir.condition}`);
    gIf.appendChild(el('circle',{cx:x,cy:y,r:7.5,fill:'#0c0f17',stroke:'#c084fc','stroke-width':1.6,
      ...(c0.not?{'stroke-dasharray':'3 2'}:{})}));
    artNode(gIf,c0.name,'ring',x,y,5.5,'#c084fc',useCdn);}

  /* SKILL ADV / DIS — small chevron badge upper-left (-160°): green up = advantage, red down = dis.
     (icons looked ugly here, so a clean chevron pair instead.) */
  /* SKILL ADV / DIS — shows the skill's REAL icon (ring green=ADV / red=DIS) + a direction chevron;
     MULTIPLE skills add echoed shadow discs out to the side (like the contest skills) = "more skills". */
  if(ir.skillAdv||ir.skillDis){const[x,y]=P(cx,cy,R-4,-160);
    const up=ir.skillAdv, col=up?'#34d399':'#ef5a5a';
    const nm=up?ir.skillAdvNames:ir.skillDisNames;
    const lbl=`Skill roll ${up?'ADV':'DIS'}${nm.length?': '+nm.join(', '):''}`;
    const gAd=part(s,'Skill Adv/Dis',lbl), extra=Math.max(0,nm.length-1);
    for(let e=extra;e>=1;e--)gAd.appendChild(el('circle',{cx:x-e*3,cy:y,r:7.5,fill:'none',stroke:col,'stroke-width':1,opacity:.35})); // echoes = more skills
    gAd.appendChild(el('circle',{cx:x,cy:y,r:7.5,fill:'#0c0f17',stroke:col,'stroke-width':1.6}));
    artNode(gAd,nm[0]||'',up?'sun':'shard',x,y,5.5,col,useCdn,true);   // noFallback: skill icon only, NO shape behind
    const d=up?1:-1; // bold chevron BELOW the disc: UP = ADV, DOWN = DIS (made larger/bolder to stand out)
    gAd.appendChild(el('path',{d:`M${x-4} ${y+8.4+d*2} L${x} ${y+8.4-d*2} L${x+4} ${y+8.4+d*2}`,fill:'none',stroke:col,'stroke-width':2.6,'stroke-linecap':'round','stroke-linejoin':'round'}));}

  /* ROLL MANIPULATION — doubled/halved/maximized/reroll etc. A small starburst near the core
     top-right: just "this ability bends the roll" (hover gives which). */
  if(ir.rollManip){const[x,y]=[cx+12,cy-12];
    const gRm=part(s,'Roll Manipulation',`Roll manipulation: ${ir.rollManip}`);
    gRm.appendChild(el('circle',{cx:x,cy:y,r:5,fill:'#0c0f17',stroke:'#fde047','stroke-width':1.2}));
    for(let i=0;i<6;i++){const a=i*60;const[x1,y1]=P(x,y,1.2,a),[x2,y2]=P(x,y,3.4,a);
      gRm.appendChild(el('line',{x1,y1,x2,y2,stroke:'#fde047','stroke-width':1,'stroke-linecap':'round'}));}}

  /* STATUS satellites: TARGET on the LEFT, CASTER on the RIGHT.
     BORDER encodes WHEN it applies: on-hit = SOLID, after-contest-fail = DOUBLE ring,
     after-cast = DOTTED; REMOVED = dashed + minus badge. PERSISTENT = extra faint halo. Color = boon/bane. */
  if(showSat&&ir.statusEntries.length){
    const drawStack=(entries:RuneStatusEntry[],x:number):void=>{const MAX=lod>=2?4:lod>=1?2:1; // fewer satellites at smaller sizes
      const extra=entries.length>MAX?entries.length-MAX:0;   // show MAX discs; overflow → semicircle on the last
      const shown=extra?entries.slice(0,MAX):entries;
      const total=shown.length;
      shown.forEach((e,i)=>{const y=cy+(i-(total-1)/2)*23;const c=compileStatus(e.it);
        const removed=e.mode==='removed', t=e.timing||'';
        const contestFail=t.includes('contest'), afterCast=t.includes('cast')&&!contestFail;
        const verb=removed?'Removes':(c.boon?'Grants':'Inflicts');
        const gS=part(s,'Status',`${verb} (${e.who}, ${t}${c.persistent?', persistent':''}): ${c.name}`);
        /* ONE ring only (no second concentric ring). Timing reads from the SINGLE border:
           removed→dashed; after-cast→dotted; after-contesting-roll-fail (common)→THIN + softened
           (reduced intensity, single ring — no double border); on-hit→solid + sparkle (below). */
        const dash=removed?'3 2':(afterCast?'1 2':null);
        const cf=contestFail&&!removed;
        gS.appendChild(el('circle',{cx:x,cy:y,r:11,fill:'#0c0f17',stroke:c.color,
          'stroke-width':cf?1.25:1.5, 'stroke-opacity':cf?0.6:1, ...(dash?{'stroke-dasharray':dash}:{})}));
        /* ON-HIT (mostly attack abilities) → a small sparkle accent, top-right of the disc. */
        if(t.includes('hit')&&!removed&&!contestFail){const sx=x+7.4,sy=y-7.4,sr=2.4;
          gS.appendChild(el('path',{d:`M${sx} ${sy-sr} L${sx+sr*0.32} ${sy-sr*0.32} L${sx+sr} ${sy} L${sx+sr*0.32} ${sy+sr*0.32} L${sx} ${sy+sr} L${sx-sr*0.32} ${sy+sr*0.32} L${sx-sr} ${sy} L${sx-sr*0.32} ${sy-sr*0.32} Z`,fill:c.color}));}
        artNode(gS,c.icon||c.name,c.motif,x,y,7.6,c.color,useCdn,true); // r7.6 keeps the (square) icon inside the disc
        if(removed){ // minus badge bottom-right of the disc = "removed"
          gS.appendChild(el('circle',{cx:x+8,cy:y+8,r:3.2,fill:'#0c0f17',stroke:c.color,'stroke-width':1}));
          gS.appendChild(el('line',{x1:x+6.3,y1:y+8,x2:x+9.7,y2:y+8,stroke:c.color,'stroke-width':1.4,'stroke-linecap':'round'}));}
        /* OVERFLOW (more statuses than fit): unobtrusive semicircle hugging the BOTTOM of the last disc
           — just signals "more applied" without a loud "+N" badge (user 2026-06-01). Count in hover. */
        if(extra&&i===total-1){
          gS.setAttribute('data-part',`Status: ${c.name} (+${extra} more ${e.who} status effect${extra>1?'s':''})`);
          gS.appendChild(el('path',{d:`M${x-10} ${y+11} A10 10 0 0 0 ${x+10} ${y+11}`,fill:'none',stroke:'#cbd5e1','stroke-width':1.6}));}
      });
    };
    drawStack(ir.statusEntries.filter((e)=>e.who==='target'), cx-R+9);
    drawStack(ir.statusEntries.filter((e)=>e.who==='caster'), cx+R-9);
  }

  /* CASTING TIME (spells) — the LEGACY no-badge layout, where AP was encoded ENTIRELY by the
     inner-ring SHAPE (diamond=1/hexagon=2/octagon=3/pentagon=reaction/circle=free) and only a true
     casting-TIMER cast (minutes/hours) got a small ⏲ emblem below the frame. Reachable only with
     apBadge:'off' (the lab's A/B), since the shipped 'rim' badge carries its own ⏲. Deliberately
     still spell-gated: menta/curse never drew this emblem, and enabling it here would drop a disc
     on the cy+40 lane the recharge disc owns when the badge is off. */
  /* …suppressed when an AP badge is on: the badge already carries the ⏲ in its own slot, and two
     clocks in the same corner is worse than none. */
  if(ir.isSpell && TIMER_CASTS.has(ir.cast) && !apVar){const apY=cy+44;
    const gAP=part(s,'Casting Time',`Casting time: ${CAST_LABEL[ir.cast]||ir.cast}`);
    gAP.appendChild(el('circle',{cx,cy:apY,r:6,fill:cc,stroke:'#0c0f17','stroke-width':1.2}));
    const t=el('text',{x:cx,y:apY+3,fill:'#0c0f17','font-size':9,'text-anchor':'middle','font-weight':'800'});
    t.textContent='⏲';gAP.appendChild(t);}

  /* AP CHANGE DURING TURN — number (or 'r'=reaction) in a teal disc, lower-right of center.
     Distinct from AP cost: modifies action economy mid-turn (e.g. resets reaction). */
  if(ir.apChange){const gAc=part(s,'AP Change',`AP change during turn: ${ir.apChange==='r'?'reaction reset':ir.apChange}`);
    /* the 'bc' plate lands on this disc's lane (x 31..69 vs its 61.5..74.5), so step it up the right
       flank when that variant is on. 'br' needs no move — the plate now sits above y=83.5. */
    /* This disc is the other bottom-band squatter, and it's the one that was capping how far the
       rim pips could spread (its left edge sits at x=61.5, right in their arc). AP owns that band
       now, so it moves too: for the rim variants out to the 45° diagonal at radius 58, which clears
       the relocated recharge disc at radius 42 (centres 16.0 apart vs 13.0 of combined radii).
       Keeping it on the SAME diagonal as recharge is deliberate — the two action-economy modifiers
       stay grouped instead of being scattered to opposite corners. */
    const bcBadge=apVar==='bc', rimBadge=apVar==='rim'||apVar==='arc';
    const[rax,ray]=P(cx,cy,58,45);
    const acx=rimBadge?rax:bcBadge?cx+30:cx+18, acy=rimBadge?ray:bcBadge?cy+24:cy+40;
    gAc.appendChild(el('circle',{cx:acx,cy:acy,r:6.5,fill:'#0c0f17',stroke:'#22d3ee','stroke-width':1.4}));
    if(ir.apChange==='r'){ // reaction regained = refresh arrow (symbol only)
      gAc.appendChild(el('path',{d:`M${acx+2.6} ${acy-2} A3 3 0 1 1 ${acx-2.6} ${acy-1}`,fill:'none',stroke:'#22d3ee','stroke-width':1.3,'stroke-linecap':'round'}));
      gAc.appendChild(el('path',{d:`M${acx+1.6} ${acy-3.6} L${acx+3.4} ${acy-1.4} L${acx+1} ${acy-1}`,fill:'#22d3ee',stroke:'none'}));}
    else{ // AP gain/loss = double-chevron direction (UP=gain / DOWN=loss), NO number
      const dir=parseInt(ir.apChange,10)>=0?1:-1;
      const chev=(yy:number)=>gAc.appendChild(el('path',{d:dir>0
        ?`M${acx-2.6} ${yy+1.4} L${acx} ${yy-1.4} L${acx+2.6} ${yy+1.4}`
        :`M${acx-2.6} ${yy-1.4} L${acx} ${yy+1.4} L${acx+2.6} ${yy-1.4}`,
        fill:'none',stroke:'#22d3ee','stroke-width':1.5,'stroke-linecap':'round','stroke-linejoin':'round'}));
      chev(acy-1.3);chev(acy+2);}}

  /* DISARMED — broken/dropped weapon mark on the TARGET (left) side. */
  if(ir.disarmed){const[x,y]=P(cx,cy,R-4,200);
    const gDz=part(s,'Disarm','Disarms the target (weapon dropped)');
    gDz.appendChild(el('circle',{cx:x,cy:y,r:6.5,fill:'#0c0f17',stroke:'#ef5a5a','stroke-width':1.4}));
    gDz.appendChild(el('path',{d:`M${x-3} ${y+3.5} L${x+2} ${y-2.5}`,fill:'none',stroke:'#ef5a5a','stroke-width':1.5,'stroke-linecap':'round'}));
    gDz.appendChild(el('path',{d:`M${x-0.5} ${y+1.5} L${x+1.8} ${y+3.5}`,fill:'none',stroke:'#ef5a5a','stroke-width':1.2,'stroke-linecap':'round'}));
    gDz.appendChild(el('path',{d:`M${x+1.5} ${y-3.8} L${x+3.6} ${y-3.8} M${x+3.6} ${y-3.8} L${x+3} ${y-1.8}`,fill:'none',stroke:'#ef5a5a','stroke-width':1,'stroke-linecap':'round'}));}

  /* GRANTED / ATTACHED ABILITY — hands you another ability. Linked-rings violet emblem, upper area (-70°). */
  /* The badge uses the granted ability's AP-SHAPE (octagon/hexagon/star/diamond/pentagon/chevron/
     circle/square) so you read its action cost at a glance; violet = "granted". +N if it grants more. */
  if(ir.grantedAbilities&&ir.grantedAbilities.length){const ga=ir.grantedAbilities, vcol='#a78bfa';
    const shown=ga.slice(0,4), extraG=ga.length-shown.length;
    const total=shown.length+(extraG>0?1:0);
    /* EACH granted ability shown as its own AP-SHAPE badge (so passive+attack+free all read), violet =
       "granted"; fanned along the upper-right arc. Overflow → +N badge. */
    shown.forEach((a,i)=>{const[x,y]=P(cx,cy,R-4,-58+i*15);const shp=TYPE_SHAPE[a.type]||'circle';
      const gGa=part(s,'Grants Ability',`Grants: ${a.name} (${TYPE_LABEL[a.type]||a.type})`);
      gGa.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:vcol,'stroke-width':1.3}));
      if(shp==='star')star(gGa,x,y,4.4,vcol); else drawFrame(gGa,x,y,4.4,shp,vcol,1.4);});
    if(extraG>0){const[x,y]=P(cx,cy,R-4,-58+shown.length*15);
      const gGa=part(s,'Grants Ability',`+${extraG} more granted ${extraG>1?'abilities':'ability'}`);
      gGa.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:vcol,'stroke-width':1.3}));
      const tg=el('text',{x,y:y+2.5,fill:vcol,'font-size':7,'text-anchor':'middle','font-weight':'800'});
      tg.textContent='+'+extraG;gGa.appendChild(tg);}}

  /* DEFENSES — resist / immune / vuln to damage type(s), stacked lower-left (115°).
     SINGLE type → show the actual DAMAGE-TYPE RUNE (element-forward), with the KIND encoded by ring
     STYLE: resist = solid ring, immune = DOUBLE ring (fully sealed), vuln = DASHED ring; ring/rune
     coloured by the element (vuln tinted red as a bane). MULTIPLE types → one neutral disc with the
     abstract symbol (½ resist / ∅ immune / ×2 vuln) + "+" badge, to avoid drawing N element runes. */
  {const defs2=[];
    if(ir.resist.length)defs2.push({kind:'resist',sym:'½',boon:true,types:ir.resist});
    if(ir.immune.length)defs2.push({kind:'immune',sym:'∅',boon:true,types:ir.immune});
    if(ir.vuln.length)defs2.push({kind:'vuln',sym:'×2',boon:false,types:ir.vuln});
    const n=defs2.length;
    defs2.forEach((d,i)=>{const a=115+(i-(n-1)/2)*17;const[x,y]=P(cx,cy,R-3,a);
      const word={resist:'Resist',immune:'Immune to',vuln:'Vulnerable to'}[d.kind];
      const gDf=part(s,'Defense',`${word}: ${d.types.join(', ')}`);
      if(d.types.length===1){
        /* element-forward: damage-type rune in the disc, ring style = which defense */
        const dc=dmg(d.types[0]).c, ec=(d.kind==='vuln')?'#ef5a5a':dc; // vuln reads red
        gDf.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:ec,'stroke-width':1.6,
          ...(d.kind==='vuln'?{'stroke-dasharray':'2 1.6'}:{})}));
        if(d.kind==='immune')gDf.appendChild(el('circle',{cx:x,cy:y,r:9,fill:'none',stroke:ec,'stroke-width':1,opacity:.7})); // double = sealed
        gDf.appendChild(motifNode(dmg(d.types[0]).g,x,y,4.6,ec,false));
      } else {
        /* multi-type → abstract symbol (anti-bloat), grey disc + "+" badge; symbol green boon / red bane */
        const ring='#cbd5e1', symCol=d.boon?'#34d399':'#ef5a5a';
        gDf.appendChild(el('circle',{cx:x,cy:y,r:7,fill:'#0c0f17',stroke:ring,'stroke-width':1.5}));
        const t=el('text',{x,y:y+2.6,fill:symCol,'font-size':d.sym.length>1?6:8,'text-anchor':'middle','font-weight':'800'});
        t.textContent=d.sym;gDf.appendChild(t);
        gDf.appendChild(el('circle',{cx:x+5.5,cy:y-5,r:2.6,fill:'#0c0f17',stroke:ring,'stroke-width':.9}));
        const p=el('text',{x:x+5.5,y:y-3.3,fill:ring,'font-size':4.5,'text-anchor':'middle','font-weight':'800'});
        p.textContent='+';gDf.appendChild(p);
      }
    });}

  /* RESET / RECHARGE — bottom, kept in-frame (cy+40).
     Recharge-per-round = AMBER disc + ROMAN numeral only (no arrow).
     Rest reset = NIGHT-BLUE disc + crescent moon. Spells offset past AP dots. */
  /* `ir.isSpell&&ap>0` is VESTIGIAL: it offsets past AP dots that spells stopped drawing when the
     casting-time shape took over the inner ring. Left in place (harmless, and it keeps the spell
     layout the owner signed off on), but the live reason to shift is now the 'bc' AP badge, which
     really does land on cy+40. */
  {let rx=cx, ry2=cy+40;
   if(ir.isSpell&&ap>0)rx=cx+24;
   /* the 'bc' plate is up to 37.3 wide (3 pips), i.e. x 31.4..68.7 — cx+24 leaves the disc's
      stroked left edge at 66.9, still inside it. cx+30 clears. */
   if(apVar==='bc')rx=cx+30;
   /* ⭐ AP OWNS THE BOTTOM RIM (owner 2026-07-24: "since everything uses AP we have to make sure
      that field is clear"). The rim variants put pips at radius R+5 centred on 90°, which is 5
      units outside this disc — with r6.5 + r4.1 they unavoidably collide, and the charge-pool
      dots at ry2+10 sit right on top of them. So for the rim variants the recharge/reset cluster
      (and its charge dots, which track rx/ry2) vacates to the 45° bottom-right diagonal, the one
      free quadrant at this radius: it clears the AP-change disc (x ≤ 74.5) and the caster status
      lane, and nothing else is drawn there. Badged types only (spell/menta/curse), so no ability
      layout moves. Audited 2026-07-30 (tools/probes/rune_ap_badge_menta_curse_audit_2026_07_30.php):
      ZERO of the 174 live menta and 110 live curses draw a recharge/reset disc at all, and the one
      menta with an AP-change disc is nowhere near the 3-caster-status collision tier — so this
      relocation is pure insurance for them, not a live layout change. */
   if(apVar==='rim'||apVar==='arc'){const[dx,dy]=P(cx,cy,42,45);rx=dx;ry2=dy;}
   if(ir.rechargeBegin){const gRc=part(s,'Recharge',
       `Recharge ${ir.rechargeBegin}${ir.rechargeEnd&&ir.rechargeEnd!==ir.rechargeBegin?'–'+ir.rechargeEnd:''} (per round)`);
     gRc.appendChild(el('circle',{cx:rx,cy:ry2,r:6.5,fill:'#b5722a',stroke:'#0c0f17','stroke-width':1.2}));
     const t=el('text',{x:rx,y:ry2+2.5,fill:'#fff','font-size':7.5,'text-anchor':'middle','font-weight':'800'});
     t.textContent=roman(ir.rechargeBegin);gRc.appendChild(t);}
   else if(ir.resetsLong||ir.resetsShort){const gRr=part(s,'Reset',
       ir.resetsLong?'Resets on long rest':'Resets on short rest');
     gRr.appendChild(el('circle',{cx:rx,cy:ry2,r:6.5,fill:'#24304a',stroke:'#0c0f17','stroke-width':1.2}));
     const k=ir.resetsLong?2.4:3.6;
     gRr.appendChild(el('path',{d:`M${rx+2.4} ${ry2-3.4} A3.8 3.8 0 1 0 ${rx+2.4} ${ry2+3.4} A${k} ${k} 0 1 1 ${rx+2.4} ${ry2-3.4} Z`,fill:'#e8ecf5'}));}
   /* charge POOL — only when >1 (else assume 1, minimalist): white dots under the moon/recharge disc. */
   const chargeCount=ir.charges??0;
   if(chargeCount>1&&(ir.rechargeBegin||ir.resetsLong||ir.resetsShort)){
     const gPc=part(s,'Charges',`${chargeCount} charges`);
     const nd=Math.min(chargeCount,6), x0=rx-(nd-1)*1.8;
     for(let i=0;i<nd;i++)gPc.appendChild(el('circle',{cx:x0+i*3.6,cy:ry2+10,r:1.3,fill:'#e8ecf5'}));}
  }

  /* ABILITY CHARGES — bottom-LEFT zone. The disc shows the REAL icon of the charge ability
     in question (icon_map png), ringed RED for consume / GREEN for gain. Quantity beside it
     uses a tiered glyph stack: double-star = 10, star = 5, dot = 1 (so Fury×7 = ★ + ·· ). */
  {const chg=ir.consumesCharges!=null?{q:ir.consumesCharges,col:'#ef5a5a',nm:ir.consumesName,
       label:`Consumes ${ir.consumesCharges} charge(s)${ir.consumesName?' of '+ir.consumesName:''}`}
     :ir.addsCharges!=null?{q:ir.addsCharges,col:'#34d399',nm:ir.addsName,
       label:`Grants ${ir.addsCharges} charge(s)${ir.addsName?' to '+ir.addsName:''}`}:null;
   if(chg){const[bx,by]=P(cx,cy,R-4,150); // bottom-left, just inside the frame
     const gCh=part(s,'Charges',chg.label);
     gCh.appendChild(el('circle',{cx:bx,cy:by,r:8,fill:'#0c0f17',stroke:chg.col,'stroke-width':1.25}));
     artNode(gCh,chg.nm,'ring',bx,by,5.4,chg.col,useCdn);  // real charge-ability icon (kept inside the disc)
     chargeQty(gCh,bx-13,by,chg.q,chg.col);              // tiered qty stack to the LEFT
   }}

  /* LEVEL — BOTTOM-CENTER plaque for SPELL / MENTA / CURSE (drawn late, ABOVE the damage spray).
     ★ = 5 levels, dice-dot = 1; cantrip/level-0 = hollow ring. Star colour by type: spell gold,
     menta = manifestation colour, curse fuchsia (the FRAME shape, incl. curse down-triangle, is untouched). */
  if((ir.isSpell||ir.itype==='menta'||ir.itype==='curse') && ir.level!=null){const lv=ir.level;
    const lvCol=ir.itype==='menta'?manifestColor(ir.manifestation):ir.itype==='curse'?'#d946ef':'#fbbf24';
    const stars=Math.floor(lv/5), dots=((lv%5)+5)%5, ng=lv<=0?1:(stars+dots);
    const sp=6.5, ly=cy+27, lx=cx-((ng-1)*sp)/2, w=Math.max(13,ng*sp+6);
    const gLv=part(s,'Level',`Level ${lv}`);
    gLv.appendChild(el('rect',{x:cx-w/2,y:ly-6.5,width:w,height:13,rx:6.5,
      fill:'#0c0f17',opacity:.85,stroke:'#e2e8f0','stroke-width':0.7,'stroke-opacity':.35}));
    if(lv<=0){
      gLv.appendChild(el('circle',{cx,cy:ly,r:3,fill:'none',stroke:lvCol,'stroke-width':1.4}));}
    else{const glyphs=[]; for(let i=0;i<stars;i++)glyphs.push('s'); for(let i=0;i<dots;i++)glyphs.push('d');
      glyphs.forEach((gk,i)=>{const gx=lx+i*sp; if(gk==='s')star(gLv,gx,ly,4,lvCol);
        else gLv.appendChild(el('circle',{cx:gx,cy:ly,r:2.4,fill:'#e2e8f0'}));});}}

  /* CONTEST → HALF: "successful contesting roll = half damage". Drawn LATE so it's ON TOP (the duration
     rings used to paint over it). A clean half-filled YELLOW disc on a DARK backing, below the roll emblem. */
  if(ir.halfOnContest){const hy=cy-R+15.5, hr=4;
    const gH=part(s,'Contest','Successful contesting roll → half damage');
    gH.appendChild(el('circle',{cx,cy:hy,r:hr,fill:'#0c0f17',stroke:'#fde047','stroke-width':1.3}));   // dark backing + ring
    gH.appendChild(el('path',{d:`M${cx} ${hy-hr} A${hr} ${hr} 0 0 1 ${cx} ${hy+hr} Z`,fill:'#fde047'}));}  // right half filled = "½"

  /* AGAINST ABILITY — a → arrow across the top signals the ability acts on the TARGET's rolls/actions
     (roll_type_against_*, triggers_*_against, …). Drawn late = on top. Red = aimed at the target. */
  if(ir.hasAgainst){const ay=3, aw=13;
    const gAg=part(s,'Against','Against ability — acts on the target’s rolls / actions');
    gAg.appendChild(el('path',{d:`M${cx-aw} ${ay} H${cx+aw} M${cx+aw-3.6} ${ay-2.4} L${cx+aw} ${ay} L${cx+aw-3.6} ${ay+2.4}`,
      fill:'none',stroke:'#ef5a5a','stroke-width':1.5,'stroke-linecap':'round','stroke-linejoin':'round'}));}

  /* CLASS / SUBCLASS REQUIREMENT — bottom-LEFT crest badge marking the ability as granted by a Class
     (bronze single ring) or Subclass (gold DOUBLE ring = the deeper tier). Shows the real
     class/subclass icon in the disc when it resolves; falls back to a chevron-tier mark (1 chevron =
     class, 2 nested = subclass) so the disc is never empty. Name(s) + any required class level live in
     the hover. Drawn late (on top). Subclass is the more specific tier, so it wins the disc + colour
     when both are present. Floated OUTSIDE the frame in the bottom-left corner so it doesn't clutter
     the interior (user 2026-06-27). */
  if(ir.reqClass.length||ir.reqSubclass.length){
    const isSub=ir.reqSubclass.length>0;
    const col=isSub?'#e3b341':'#cd7f32';                 // subclass = gold, class = bronze
    const nm=isSub?ir.reqSubclass[0]:ir.reqClass[0];
    const[bx,by]=P(cx,cy,R+14,135);                      // bottom-left, floated further into the corner
    const lbl=[ir.reqClass.length?'Class: '+ir.reqClass.join(', '):null,
               ir.reqSubclass.length?'Subclass: '+ir.reqSubclass.join(', '):null,
               ir.reqClassLevel?'requires class level '+ir.reqClassLevel:null].filter(Boolean).join(' · ');
    const gCl=part(s,'Class Req',lbl);
    gCl.appendChild(el('circle',{cx:bx,cy:by,r:7.4,fill:'#0c0f17',stroke:col,'stroke-width':1})); // thin single border; class vs subclass = colour (bronze/gold) + chevron count
    /* chevron-tier fallback (drawn UNDER the icon; the CDN crest covers it when it loads) */
    const chev=(yo:number)=>gCl.appendChild(el('path',{d:`M${bx-3.2} ${by+yo+1.6} L${bx} ${by+yo-1.6} L${bx+3.2} ${by+yo+1.6}`,
      fill:'none',stroke:col,'stroke-width':1.2,'stroke-linecap':'round','stroke-linejoin':'round'}));
    if(isSub){chev(-1.9);chev(1.9);}else chev(0);
    artNode(gCl,nm,'ring',bx,by,6.2,col,useCdn);         // real class/subclass crest, enlarged to fill the disc
  }

  /* GENERIC TIER (Step 3) — fills the remainder: every visible non-curated layer as an outer-arc
     token. Curated zones above always win; this only adds marks nothing else drew. Flag-gated. */
  if(opt.gen&&lod>=2)renderGenericTokens(s,ir,cx,cy,R);  // generic-token arc = full detail only

  /* AP COST BADGE — drawn LAST so it sits on top of every satellite/token it overlaps. Tagged
     'Action Cost' (LABEL_LOD 0) so it survives into the compact tier: cost is the one fact you
     need at every size. Off unless a variant is selected. */
  if(apVar)renderApBadge(s,ir,cx,cy,R,apVar);

  /* LOD PRUNE — remove element groups whose min-LOD exceeds the current tier (by data-tag).
     Identity (frame/core/roll/trigger/against) = 0 (always); generic tokens = 2 (full only);
     everything else defaults to 1 (standard+). One pass, so no per-block guards needed. */
  s.querySelectorAll('[data-tag]').forEach((g:Element)=>{
    const ml=LABEL_LOD[g.getAttribute('data-tag') as string]??1;
    if(ml>lod)g.remove();});
  return s;
}

function teamArrows(g:SVGElement,cx:number,cy:number,outward:boolean,col:string):void{
  const R=46, n=8;
  for(let i=0;i<n;i++){const a=-90+i*360/n;
    const tip  = outward ? R+4 : R-4;   // apex points out (enemy) or in (friend)
    const base = outward ? R-1 : R+1;
    const[ax,ay]=P(cx,cy,tip,a);
    const[b1x,b1y]=P(cx,cy,base,a-2.6);
    const[b2x,b2y]=P(cx,cy,base,a+2.6);
    g.appendChild(el('polygon',{points:`${ax},${ay} ${b1x},${b1y} ${b2x},${b2y}`,fill:col,opacity:.85}));}
}
/* tiered quantity stack (vertical): double-star = 10, star = 5, dot = 1. Cap ~6 glyphs. */
function chargeQty(g:SVGElement,x:number,y:number,q:number,col:string):void{
  const tens=Math.floor(q/10), fives=Math.floor((q%10)/5), ones=(q%10)%5;
  const glyphs:string[]=[];
  for(let i=0;i<tens;i++)glyphs.push('star2');
  for(let i=0;i<fives;i++)glyphs.push('star');
  for(let i=0;i<ones;i++)glyphs.push('dot');
  const n=Math.min(glyphs.length,6), y0=y-(n-1)*4/2;
  for(let i=0;i<n;i++){const gy=y0+i*4, k=glyphs[i];
    if(k==='dot')g.appendChild(el('circle',{cx:x,cy:gy,r:1.5,fill:col}));
    else if(k==='star')star(g,x,gy,2.4,col);
    else{star(g,x-1.6,gy,2.1,col);star(g,x+1.6,gy,2.1,col);}}  // double star = 10
}
function aoeBadge(g:SVGElement,x:number,y:number,aoe:string,col:string):void{const o={fill:'none',stroke:col,'stroke-width':1.7,'stroke-linejoin':'round','stroke-linecap':'round'};
  const fill={fill:col,'fill-opacity':.18};
  switch(aoe){
    case 'Sphere':
      g.appendChild(el('circle',{cx:x,cy:y,r:8.5,...o,...fill}));
      /* equator ellipse DASHED = it sits behind the sphere surface (2D depth cue); no highlight dot */
      g.appendChild(el('ellipse',{cx:x,cy:y,rx:8.5,ry:3.2,...o,'stroke-width':1,'stroke-opacity':.5,'stroke-dasharray':'2 2'}));break;
    case 'Cylinder':
      g.appendChild(el('path',{d:`M${x-8} ${y-5} V${y+5} M${x+8} ${y-5} V${y+5}`,...o}));
      g.appendChild(el('ellipse',{cx:x,cy:y-5,rx:8,ry:3.2,...o,...fill}));
      g.appendChild(el('path',{d:`M${x-8} ${y+5} A8 3.2 0 0 0 ${x+8} ${y+5}`,...o}));break;
    case 'Cube':
      g.appendChild(el('path',{d:`M${x-6.5} ${y-2.5} L${x} ${y-6.5} L${x+6.5} ${y-2.5} L${x+6.5} ${y+5} L${x} ${y+9} L${x-6.5} ${y+5} Z`,...o,...fill}));
      g.appendChild(el('path',{d:`M${x-6.5} ${y-2.5} L${x} ${y+1.5} L${x+6.5} ${y-2.5} M${x} ${y+1.5} V${y+9}`,...o,'stroke-width':1.1}));break;
    case 'Square': g.appendChild(el('rect',{x:x-7,y:y-7,width:14,height:14,rx:1.5,...o,...fill}));break;
    case 'Cone': g.appendChild(el('path',{d:`M${x-7.5} ${y+6.5} L${x-7.5} ${y-6.5} A15 15 0 0 1 ${x+7.5} ${y+6.5} Z`,...o,...fill}));break;
    case 'Line': g.appendChild(el('rect',{x:x-3,y:y-8.5,width:6,height:17,rx:2,...o,...fill}));break;
    default: g.appendChild(el('circle',{cx:x,cy:y,r:8.5,...o,...fill}));
  }}

function rollTypeGlyph(g:SVGElement,x:number,y:number,r:number,col:string,kind:string):void{const o={stroke:col,'stroke-width':1.6,fill:'none','stroke-linecap':'round','stroke-linejoin':'round'};
  g.appendChild(el('circle',{cx:x,cy:y,r:r+1,fill:'#0c0f17',stroke:col,'stroke-width':1.2}));
  /* shared sub-glyphs */
  const burst=(rad?:number):void=>{rad=rad||r;[0,60,120,180,240,300].forEach(a=>{const[x1,y1]=P(x,y,rad*0.35,a),[x2,y2]=P(x,y,rad,a);g.appendChild(el('line',{x1,y1,x2,y2,...o}));});};
  const plus=(rad?:number):void=>{rad=rad||r-1;g.appendChild(el('path',{d:`M${x} ${y-rad} V${y+rad} M${x-rad} ${y} H${x+rad}`,...o,'stroke-width':2}));};
  const sword=()=>{g.appendChild(el('path',{d:`M${x-r*0.65} ${y+r*0.65} L${x+r*0.55} ${y-r*0.55}`,...o,'stroke-width':1.8})); // blade
    g.appendChild(el('path',{d:`M${x-r*0.05} ${y+r*0.45} L${x+r*0.35} ${y+r*0.05}`,...o,'stroke-width':1.4}));};       // crossguard
  const arrow=()=>{g.appendChild(el('path',{d:`M${x-r*0.6} ${y+r*0.6} L${x+r*0.6} ${y-r*0.6}`,...o,'stroke-width':1.6}));
    g.appendChild(el('path',{d:`M${x+r*0.1} ${y-r*0.6} L${x+r*0.6} ${y-r*0.6} L${x+r*0.6} ${y-r*0.1}`,...o,'stroke-width':1.4}));}; // head
  const spark=()=>{const sx=x-r*0.65,sy=y-r*0.65;[0,90,45,135].forEach(a=>{const[x1,y1]=P(sx,sy,0.6,a),[x2,y2]=P(sx,sy,2,a);g.appendChild(el('line',{x1,y1,x2,y2,stroke:col,'stroke-width':1,'stroke-linecap':'round'}));});};
  const downChev=()=>{g.appendChild(el('path',{d:`M${x-2.6} ${y+r*0.5} L${x} ${y+r*0.95} L${x+2.6} ${y+r*0.5}`,...o,'stroke-width':1.4}));};
  const check=()=>{g.appendChild(el('path',{d:`M${x-r*0.7} ${y+r*0.1} L${x-r*0.1} ${y+r*0.7} L${x+r*0.8} ${y-r*0.6}`,...o,'stroke-width':2}));};
  const diamond=()=>g.appendChild(el('path',{d:`M${x} ${y-r*0.85} L${x+r*0.7} ${y} L${x} ${y+r*0.85} L${x-r*0.7} ${y} Z`,...o}));
  const dotc=()=>g.appendChild(el('circle',{cx:x,cy:y,r:r*0.18,fill:col,stroke:'none'}));
  const sparkle=()=>{[0,90,180,270].forEach(a=>{const[px,py]=P(x,y,r*0.85,a);g.appendChild(el('line',{x1:x,y1:y,x2:px,y2:py,...o,'stroke-width':1.5}));});[45,135,225,315].forEach(a=>{const[px,py]=P(x,y,r*0.42,a);g.appendChild(el('line',{x1:x,y1:y,x2:px,y2:py,...o,'stroke-width':1}));});};
  const cycle=()=>{g.appendChild(el('path',{d:`M${x+r*0.55} ${y-r*0.35} A${r*0.68} ${r*0.68} 0 1 0 ${x+r*0.7} ${y+r*0.2}`,...o,'stroke-width':1.5}));g.appendChild(el('path',{d:`M${x+r*0.2} ${y-r*0.5} L${x+r*0.58} ${y-r*0.32} L${x+r*0.78} ${y-r*0.66}`,...o,'stroke-width':1.3}));};
  switch(kind){
    case 'contest': g.appendChild(el('path',{d:`M${x-r+1} ${y} L${x-1} ${y} M${x-3.5} ${y-2.6} L${x-1} ${y} L${x-3.5} ${y+2.6} M${x+r-1} ${y} L${x+1} ${y} M${x+3.5} ${y-2.6} L${x+1} ${y} L${x+3.5} ${y+2.6}`,...o}));break;
    case 'atk_melee': sword();break;
    case 'atk_ranged': arrow();break;
    case 'atk_melee_spell': sword();spark();break;
    case 'atk_ranged_spell': arrow();spark();break;
    case 'heal': plus();break;
    case 'heal_reduce': plus(r*0.6);downChev();break;
    case 'heal_dot': plus(r*0.7);g.appendChild(el('path',{d:`M${x+r*0.55} ${y-r*0.85} A${r*0.55} ${r*0.55} 0 1 1 ${x+r*0.9} ${y-r*0.4}`,...o,'stroke-width':1}));break;
    case 'dot': // damage over time = clock + drip
      g.appendChild(el('circle',{cx:x,cy:y,r:r*0.72,...o}));
      g.appendChild(el('path',{d:`M${x} ${y} V${y-r*0.5} M${x} ${y} L${x+r*0.42} ${y+r*0.12}`,...o,'stroke-width':1.5}));
      g.appendChild(el('circle',{cx:x,cy:y+r*1.15,r:r*0.22,fill:col,stroke:'none'}));break;
    case 'skill': check();break;
    case 'skill_spell': check();spark();break;
    case 'dmg_reduce': burst(r*0.78);downChev();break;
    case 'dmg_object': burst(r*0.62);g.appendChild(el('rect',{x:x-r*0.42,y:y-r*0.42,width:r*0.84,height:r*0.84,rx:.6,...o,'stroke-width':1}));break;
    case 'summon':
      g.appendChild(el('path',{d:`M${x-r*0.6} ${y+r*0.4} A${r*0.6} ${r*0.6} 0 0 0 ${x+r*0.6} ${y+r*0.4}`,...o}));
      g.appendChild(el('path',{d:`M${x} ${y+r*0.5} V${y-r*0.6} M${x-r*0.4} ${y-r*0.15} L${x} ${y-r*0.6} L${x+r*0.4} ${y-r*0.15}`,...o,'stroke-width':1.8}));break;
    case 'shapeshift': // two opposed curved arrows = transformation cycle
      g.appendChild(el('path',{d:`M${x-r*0.7} ${y-r*0.15} A${r*0.7} ${r*0.7} 0 0 1 ${x+r*0.5} ${y-r*0.5}`,...o,'stroke-width':1.5}));
      g.appendChild(el('path',{d:`M${x+r*0.2} ${y-r*0.7} L${x+r*0.6} ${y-r*0.55} L${x+r*0.5} ${y-r*0.15}`,...o,'stroke-width':1.3}));
      g.appendChild(el('path',{d:`M${x+r*0.7} ${y+r*0.15} A${r*0.7} ${r*0.7} 0 0 1 ${x-r*0.5} ${y+r*0.5}`,...o,'stroke-width':1.5}));
      g.appendChild(el('path',{d:`M${x-r*0.2} ${y+r*0.7} L${x-r*0.6} ${y+r*0.55} L${x-r*0.5} ${y+r*0.15}`,...o,'stroke-width':1.3}));break;
    case 'mana': diamond();dotc();break;                                   // Increase Mana
    case 'mana_reduce': diamond();downChev();break;                        // Reduce Mana
    case 'mana_drain': diamond();g.appendChild(el('path',{d:`M${x} ${y+r*0.2} V${y+r*1.05} M${x-r*0.3} ${y+r*0.72} L${x} ${y+r*1.05} L${x+r*0.3} ${y+r*0.72}`,...o,'stroke-width':1.3}));break; // Mana Drain
    case 'luck': sparkle();break;                                          // Increase Luck
    case 'luck_reduce': sparkle();downChev();break;                        // Reduce Luck
    case 'essence': {const ep:Point[]=[[x,y-r*0.55],[x-r*0.6,y+r*0.5],[x+r*0.6,y+r*0.5]];ep.forEach(p=>g.appendChild(el('circle',{cx:p[0],cy:p[1],r:r*0.27,fill:col,stroke:'none'})));}break; // Essence (tri-essence dots)
    case 'regen': cycle();g.appendChild(el('path',{d:`M${x-r*0.26} ${y} H${x+r*0.26} M${x} ${y-r*0.26} V${y+r*0.26}`,...o,'stroke-width':1.3}));break; // Regeneration (cycle + life plus)
    case 'vitality': g.appendChild(el('path',{d:`M${x} ${y+r*0.7} C${x-r*1.05} ${y-r*0.15} ${x-r*0.35} ${y-r*0.8} ${x} ${y-r*0.2} C${x+r*0.35} ${y-r*0.8} ${x+r*1.05} ${y-r*0.15} ${x} ${y+r*0.7} Z`,...o}));break; // (Temp) Vitality
    case 'recharge': cycle();dotc();break;                                 // Recharge Ability
    default: burst();   // plain Damage Roll
  }}
function star(g:SVGElement,x:number,y:number,r:number,col:string):void{let d='';for(let i=0;i<10;i++){const rr=i%2?r*0.45:r;const a=-90+i*36;
  const[px,py]=P(x,y,rr,a);d+=(i?'L':'M')+px+' '+py+' ';}d+='Z';g.appendChild(el('path',{d,fill:col}));}
/* speed-change motif: wing (flying) / fin (swimming) / boot (everything ground). */
/* one UNIQUE motif per speed type (was wing/fin/boot only — collapsed 5 types to a boot). */
function speedMotif(g:SVGElement,x:number,y:number,kind:string,col:string):void{const o={stroke:col,'stroke-width':1.3,fill:'none','stroke-linecap':'round','stroke-linejoin':'round'};
  const add=(d:string,ex?:SvgAttrs)=>g.appendChild(el('path',{d,...o,...(ex||{})}));
  switch(kind){
    case 'flying': add(`M${x-4} ${y-2} Q${x} ${y-5} ${x+4} ${y-2} Q${x+1} ${y-1} ${x+4} ${y+1.5} Q${x} ${y-1} ${x-4} ${y+2} Z`);break;       // wing
    case 'swimming': add(`M${x-4} ${y+3} L${x} ${y-4} L${x+4} ${y+3}`);add(`M${x-4} ${y+3} Q${x} ${y+1} ${x+4} ${y+3}`,{'stroke-width':1});break; // fin
    case 'climbing': add(`M${x-4} ${y+4} L${x-1.3} ${y-4} L${x+1.3} ${y+1} L${x+4} ${y-3}`);break;                                              // zigzag ascent
    case 'jumping': add(`M${x-4} ${y+4} Q${x} ${y-6} ${x+4} ${y+4}`);break;                                                                     // leap arc
    case 'hovering': add(`M${x-4} ${y-1.5} H${x+4}`);add(`M${x-3} ${y+1.5} H${x+3}`,{'stroke-dasharray':'1.5 1.5'});add(`M${x-2} ${y+4} H${x+2}`,{'stroke-dasharray':'1.5 1.5'});break; // floating lines
    case 'burrowing': add(`M${x} ${y-4} V${y+1.5} M${x-2.4} ${y-1} L${x} ${y+2} L${x+2.4} ${y-1}`);add(`M${x-4} ${y+4} H${x+4}`,{'stroke-dasharray':'1.5 1.5'});break; // down-arrow into ground
    default: add(`M${x-2.5} ${y-4} V${y+1} Q${x-2.5} ${y+3} ${x-0.5} ${y+3} H${x+4} Q${x+3} ${y+1} ${x+1} ${y+1} V${y-4} Z`);                   // walking = boot
  }}
function motifNode(kind:string,x:number,y:number,r:number,color:string,filled:boolean):SVGElement{
  const grp=el('g'),o={stroke:color,'stroke-width':1.4,fill:filled?color:'none','stroke-linejoin':'round','stroke-linecap':'round'};
  const add=(t:string,a:SvgAttrs)=>grp.appendChild(el(t,a));
  switch(kind){
    case 'flame':add('path',{d:`M${x} ${y-r} q ${r*0.9} ${r*0.7} 0 ${r*2} q ${-r*0.9} ${-r*0.7} 0 ${-r*2} Z`,...o});break;
    case 'bolt':add('path',{d:`M${x+r*0.4} ${y-r} L${x-r*0.4} ${y} L${x+r*0.1} ${y} L${x-r*0.4} ${y+r} L${x+r*0.5} ${y-r*0.2} L${x} ${y-r*0.2} Z`,...o});break;
    case 'sun':{add('circle',{cx:x,cy:y,r:r*0.45,...o});for(let i=0;i<8;i++){const a=i*45;
      const[x1,y1]=P(x,y,r*0.65,a),[x2,y2]=P(x,y,r,a);add('line',{x1,y1,x2,y2,stroke:color,'stroke-width':1.2});}break;}
    case 'skull':add('circle',{cx:x,cy:y-r*0.15,r:r*0.7,...o});
      add('circle',{cx:x-r*0.28,cy:y-r*0.15,r:r*0.16,fill:'#0c0f17',stroke:'none'});
      add('circle',{cx:x+r*0.28,cy:y-r*0.15,r:r*0.16,fill:'#0c0f17',stroke:'none'});break;
    case 'shard':add('path',{d:`M${x} ${y-r} L${x+r*0.6} ${y} L${x} ${y+r} L${x-r*0.6} ${y} Z`,...o});break;
    case 'drop':add('path',{d:`M${x} ${y-r} q ${r*0.8} ${r} 0 ${r*1.7} q ${-r*0.8} ${-r*0.7} 0 ${-r*1.7} Z`,...o});break;
    case 'spiral':{let d=`M${x} ${y}`;for(let t=0;t<10;t+=0.3){const rr=r*t/10;d+=` L${x+rr*Math.cos(t)} ${y+rr*Math.sin(t)}`;}
      add('path',{d,fill:'none',stroke:color,'stroke-width':1.3});break;}
    default:add('circle',{cx:x,cy:y,r:r*0.7,...o});
  }return grp;}

/* typeBadge(itemType,size) — a SMALL standalone chip for the ability-TYPE indicator (the little
   icon that used to be a flat full_ability.png / quick_ability.png / menta.png etc). Draws the SAME
   shape + colour vocabulary the medallion frame uses (TYPE_SHAPE / TYPE_COLOR) so the chip "rhymes"
   with the big sigil, PLUS an AP cue (TYPE_AP) because at ~25px the polygon side-count alone can't
   tell full(octagon)/ability(hexagon)/menta(heptagon) apart — colour + AP pips carry the load.
   Self-contained: reads only the static TYPE_* maps, NOT window.FT_RUNE, so it renders on any page
   that loads the bundle. Returns an <svg> (viewBox 0 0 100 100) the caller scales via width/height. */
function typeBadge(itemType:string,size=25):SVGElement{
  const shape=TYPE_SHAPE[itemType]||'circle';
  const color=TYPE_COLOR[itemType]||FAMILY_HUE.ability||'#7aa2ff';
  const ap=TYPE_AP[itemType];
  const s=svgEl(size), g=el('g'), cx=50, cy=50, r=42;
  /* BLOOD DROP (sanguine) — pointed top, bulbous bottom; a filled version of the medallion's sanguine
     teardrop frame (renderSigil). shapePoints has no polygon for a curved drop, so draw the path here. */
  if(shape==='teardrop'){
    g.appendChild(el('path',{d:`M${cx} ${cy-r} C${cx+r*0.92} ${cy-r*0.25} ${cx+r} ${cy+r*0.45} ${cx} ${cy+r} C${cx-r} ${cy+r*0.45} ${cx-r*0.92} ${cy-r*0.25} ${cx} ${cy-r} Z`,
      fill:color,'fill-opacity':0.92,stroke:color,'stroke-width':5,'stroke-linejoin':'round'}));
    s.appendChild(g);return s;}
  const pts=shapePoints(cx,cy,r,shape);
  if(pts)g.appendChild(el('polygon',{points:pts.map(p=>p[0]+','+p[1]).join(' '),
    fill:color,'fill-opacity':0.92,stroke:color,'stroke-width':5,'stroke-linejoin':'round'}));
  else g.appendChild(el('circle',{cx,cy,r,fill:color,'fill-opacity':0.92,stroke:color,'stroke-width':5}));
  const ink=glyphInk(color);
  /* AP cue, in contrasting ink: any single-character TYPE_AP string is drawn as that LETTER
     (reaction 'r'→R, passive 'p'→P, general_ability 'g'→G); 1–3 AP = that many pips;
     free (0 AP) and curse/menta (no TYPE_AP entry) show shape+colour only. */
  if(typeof ap==='string'&&ap.length===1){
    const t=el('text',{x:cx,y:cy+1,fill:ink,'font-size':46,'font-weight':'700','text-anchor':'middle',
      'dominant-baseline':'central','font-family':'system-ui,Segoe UI,sans-serif'});
    t.textContent=ap.toUpperCase();g.appendChild(t);
  }else if(typeof ap==='number'&&ap>0){
    const gap=20,x0=cx-(ap-1)*gap/2;
    for(let i=0;i<ap;i++)g.appendChild(el('circle',{cx:x0+i*gap,cy,r:6.5,fill:ink}));
  }
  s.appendChild(g);return s;}

// ---- public exports (consumed by index.ts + the admin preview chrome) ----
export { compile, compileStatus, renderSigil, setRuneCatalog, dmg, durLabel, typeAccent, typeAccent2, TYPE_LABEL, CAST_LABEL, typeBadge };
